import React, { useState, useRef, useEffect, useCallback } from "react";
import Card from "components/card";
import moment from "moment";
import { BsChevronDown, BsThreeDotsVertical } from "react-icons/bs";
import { MdEdit, MdClose, MdDelete } from "react-icons/md";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { FiPlus } from "react-icons/fi";
import { Info } from "lucide-react";
import Switch from "components/switch";
import DateTimeRangePicker from "components/fields/DateTimeRangePicker";
import PromotionService from "services/promotion.service";
import CoinPackagesService from "services/coin-packages.service";
import ReferralService from "services/referral.service";
import DarkThemeService from "services/dark-theme.service";
import EmojiService from "services/emoji.service";
import VerificationBadgeService from "services/verificationbadge.service";
import Loader from "assets/img/loader.gif";
import Toast from "components/toaster";
import GooglePagination from "components/pagination/GooglePagination";
import TooltipHorizon from "components/tooltip";
import ExpandableDescription from "components/fields/ExpandableDescription";
import Select from "react-select";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Module-level tabCache for persistent data across component mounts/unmounts
const tabCache = {
  "promo-code": {
    data: null,
    firstLoad: true,
  },
  "coin-packages": {
    data: null,
    firstLoad: true,
  },
  "pricing-reference": {
    data: null,
    firstLoad: true,
  },
};

// Regular Coin Package Card Component (non-sortable for main grid)
const CoinPackageCard = ({ item, index, itemId, isDeleted, isActive, activeCoinPackageDropdown, openCoinPackageMenuId, coinPackageStatusDropdownRefs, coinPackageMenuDropdownRefs, setActiveCoinPackageDropdown, setOpenCoinPackageMenuId, handleCoinPackageStatusChange, handleCoinPackageEditClick, handleCoinPackageDeleteClick }) => {
  return (
    <div className="h-full">
      <Card
        extra="w-full h-full p-4 md:p-5 shadow-sm hover:shadow-md transition-shadow !bg-[#192242] !rounded-xl"
      >
        <div className="flex flex-col h-full">
          <div className="flex-1">
            <div className="flex-1 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-[19px] font-bold text-white">
                  {item.package_name || item.packageName || "-"}
                </h3>

                <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <div
                    className="relative"
                    ref={(el) => {
                      if (el) {
                        coinPackageStatusDropdownRefs.current[itemId] = el;
                      } else {
                        delete coinPackageStatusDropdownRefs.current[itemId];
                      }
                    }}
                  >
                    <div
                      className={`flex w-22 h-7 items-center justify-between rounded-lg 
                         ${
        isDeleted
          ? "bg-gray-600/20 cursor-not-allowed opacity-75 text-gray-400"
          : isActive
          ? "bg-green-500/20 cursor-pointer text-green-500"
          : "bg-[rgba(255,37,37,0.1)] cursor-pointer text-[rgba(240,102,112,0.835)]"
      } px-3 py-1.5 text-sm font-bold`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isDeleted) {
                          setActiveCoinPackageDropdown(
                            activeCoinPackageDropdown === itemId
                              ? null
                              : itemId
                          );
                        }
                      }}
                    >
                      <span className="mr-2">{isActive ? "Active" : "Inactive"}</span>
                      {!isDeleted && (
                        <BsChevronDown className="h-3 w-3 ml-1" style={{ stroke: "currentColor", strokeWidth: "1", fill: "none" }} />
                      )}
                    </div>

                    {activeCoinPackageDropdown === itemId && !isDeleted && (
                      <div
                        className={`absolute right-0 z-50 mt-1 ${isActive ? 'w-22' : 'w-24'} h-8 rounded-md bg-[#0b1437] border border-gray-600 p-1 shadow-lg flex items-center justify-center`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isActive ? (
                          <button
                            className="block w-full rounded-md bg-gray-600 px-3.5 py-0.5 text-left text-sm font-bold text-white hover:bg-gray-700 transition-colors"
                            onClick={() => {
                              handleCoinPackageStatusChange(itemId, "inactive");
                            }}
                          >
                            Inactive
                          </button>
                        ) : (
                          <button
                            className="block w-full rounded-md bg-gray-600 px-2.5 py-0.5 text-center text-sm font-bold text-white hover:bg-gray-700 transition-colors"
                            onClick={() => {
                              handleCoinPackageStatusChange(itemId, "active");
                            }}
                          >
                            Active
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div
                    className="relative"
                    ref={(el) => {
                      if (el) {
                        coinPackageMenuDropdownRefs.current[itemId] = el;
                      } else {
                        delete coinPackageMenuDropdownRefs.current[itemId];
                      }
                    }}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenCoinPackageMenuId(
                          openCoinPackageMenuId === itemId
                            ? null
                            : itemId
                        );
                      }}
                      className="flex items-center justify-center h-7 w-7 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                      title="Menu"
                    >
                      <BsThreeDotsVertical className="h-4 w-4 text-white/90" />
                    </button>

                    {openCoinPackageMenuId === itemId && (
                      <div
                        className="absolute right-0 z-50 mt-1 w-28 rounded-md bg-[#0b1437] border border-gray-600 shadow-lg py-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isDeleted) {
                              handleCoinPackageEditClick(item);
                              setOpenCoinPackageMenuId(null);
                            }
                          }}
                          disabled={isDeleted}
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-white/90 hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <MdEdit className="h-4 w-4" />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isDeleted) {
                              handleCoinPackageDeleteClick(item);
                            }
                          }}
                          disabled={isDeleted}
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <MdDelete className="h-4 w-4" />
                          <span>Delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center">
                  <span className="text-xl 3xl:text-2xl font-bold text-white">
                    ${item.price || "--"}
                  </span>
                  <span className="text-xl 3xl:text-2xl font-bold text-white mx-1">/</span>
                  <span
                    className="text-xl 3xl:text-2xl font-bold"
                    style={{
                      background: "linear-gradient(216.93deg, #1DB4FF 18.65%, #E934F5 83.43%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text"
                    }}
                  >
                    {item.coins || "--"} Coins
                  </span>
                </div>

                {item.description && (
                  <>
                    <div className="-mx-1 my-1 border-t border-gray-800"></div>
                    <div>
                      <div className="flex flex-col gap-1">
                        <span className="text-sm 3xl:text-sm font-medium text-gray-400  whitespace-nowrap">
                          Description:
                        </span>
                        <ExpandableDescription
                          text={item.description}
                          maxLines={2}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {!isActive && !isDeleted && (
              <div className="mt-3">
                <div className="flex items-start gap-1.5 rounded-md px-3 py-2 relative overflow-hidden">
                  <div className="absolute inset-0 bg-white/10 rounded-md"></div>
                  <div className="relative z-10 flex items-start gap-1.5 w-full">
                    <IoMdInformationCircleOutline className="h-3.5 w-3.5 text-white/90 flex-shrink-0 mt-0.1" />
                    <p className="text-xs font-medium text-white/90 leading-relaxed" style={{
                      letterSpacing: "1px",
                      lineHeight: "1.3",
                    }}>
                      Coin package is deactivated by admin.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {isDeleted && (
              <div className="mt-3">
                <div className="flex items-start gap-1.5 rounded-md px-3 py-2 relative overflow-hidden">
                  <div className="absolute inset-0 bg-white/10 rounded-md"></div>
                  <div className="relative z-10 flex items-start gap-1.5 w-full">
                    <IoMdInformationCircleOutline className="h-3.5 w-3.5 text-white/90 flex-shrink-0 mt-0.1" />
                    <p className="text-xs font-medium text-white/90 leading-relaxed" style={{
                      letterSpacing: "1px",
                      lineHeight: "1.3",
                    }}>
                      Coin package permanently deleted.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

// Sortable Coin Package Row Component (for modal reordering)
const SortableCoinPackageRow = ({ item, index, isDeleted, isActive, activeId, overId }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isOver = overId === item.id && activeId !== item.id;
  const isDraggingItem = activeId === item.id;

  return (
    <>
      {/* Drag line indicator */}
      {isOver && (
        <div className="h-0.5 bg-blue-500 mb-2 mx-2 rounded-full" />
      )}
      <div
        ref={setNodeRef}
        style={{ ...style, touchAction: 'none' }}
        {...attributes}
        {...listeners}
        className={`cursor-move flex items-center gap-3 p-3 rounded-lg border border-gray-700 bg-[#192242] hover:bg-[#1a2748] transition-colors ${isDragging || isDraggingItem ? 'shadow-lg opacity-50' : ''}`}
      >
        {/* Order Number */}
        <div className="flex-shrink-0 w-6 text-center text-white/70 font-medium">
          {index + 1}
        </div>

        {/* Content Section */}
        <div className="flex-1 min-w-0">
          {/* Package Name */}
          <div className="mb-1">
            <h3 className="text-base font-bold text-white">
              {item.package_name || item.packageName || "-"}
            </h3>
          </div>

          {/* Price + Coins Summary */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white">
              ${item.price || "--"}
            </span>
            <span className="text-sm font-medium text-white">/</span>
            <span
              className="text-sm font-bold"
              style={{
                background: "linear-gradient(216.93deg, #1DB4FF 18.65%, #E934F5 83.43%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text"
              }}
            >
              {item.coins || "--"} Coins
            </span>
          </div>
        </div>

        {/* Drag Handle - 6-dot grip icon on the right */}
        <div className="flex-shrink-0 flex items-center justify-center text-white/50 pointer-events-none">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
          >
            {/* Top row - 2 dots */}
            <circle cx="6" cy="5" r="1.5" fill="currentColor" opacity="0.6" />
            <circle cx="14" cy="5" r="1.5" fill="currentColor" opacity="0.6" />
            {/* Middle row - 2 dots */}
            <circle cx="6" cy="10" r="1.5" fill="currentColor" opacity="0.6" />
            <circle cx="14" cy="10" r="1.5" fill="currentColor" opacity="0.6" />
            {/* Bottom row - 2 dots */}
            <circle cx="6" cy="15" r="1.5" fill="currentColor" opacity="0.6" />
            <circle cx="14" cy="15" r="1.5" fill="currentColor" opacity="0.6" />
          </svg>
        </div>
      </div>
    </>
  );
};

// Sortable Promo Code Row Component (for modal reordering)
const SortablePromoCodeRow = ({ item, index, activeId, overId }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isOver = overId === item.id && activeId !== item.id;
  const isDraggingItem = activeId === item.id;

  return (
    <>
      {/* Drag line indicator */}
      {isOver && (
        <div className="h-0.5 bg-blue-500 mb-2 mx-2 rounded-full" />
      )}
      <div
        ref={setNodeRef}
        style={{ ...style, touchAction: 'none' }}
        {...attributes}
        {...listeners}
        className={`cursor-move flex items-start gap-3 p-3 rounded-lg border border-gray-700 bg-[#192242] hover:bg-[#1a2748] transition-colors ${isDragging || isDraggingItem ? 'shadow-lg opacity-50' : ''}`}
      >
        {/* Order Number */}
        <div className="flex-shrink-0 w-6 text-center text-white/70 font-medium pt-0.5">
          {index + 1}
        </div>

        {/* Content Section */}
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          {/* First Line: Promo Code Name */}
          <div className="min-w-0">
            <h3 className="text-base font-bold text-white truncate">
              {item.code ? item.code.charAt(0).toUpperCase() + item.code.slice(1).toLowerCase() : "-"}
            </h3>
          </div>

          {/* Second Line: Discount Value and Max Redemptions */}
          <div className="flex items-center gap-4">
            {/* Discount Value */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-white/70">Discount Value:</span>
              <span className="text-sm font-medium text-white">
                {item.discountValue || "-"}
              </span>
            </div>

            {/* Max Redemptions */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-white/70">Max Redemptions:</span>
              <span className="text-sm font-medium text-white">
                {item.maxUsers || "--"}
              </span>
            </div>
          </div>
        </div>

        {/* Drag Handle - 6-dot grip icon on the right center */}
        <div 
          className="flex-shrink-0 flex items-center justify-center text-white/50 self-center pointer-events-none"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
          >
            {/* Top row - 2 dots */}
            <circle cx="6" cy="5" r="1.5" fill="currentColor" opacity="0.6" />
            <circle cx="14" cy="5" r="1.5" fill="currentColor" opacity="0.6" />
            {/* Middle row - 2 dots */}
            <circle cx="6" cy="10" r="1.5" fill="currentColor" opacity="0.6" />
            <circle cx="14" cy="10" r="1.5" fill="currentColor" opacity="0.6" />
            {/* Bottom row - 2 dots */}
            <circle cx="6" cy="15" r="1.5" fill="currentColor" opacity="0.6" />
            <circle cx="14" cy="15" r="1.5" fill="currentColor" opacity="0.6" />
          </svg>
        </div>
      </div>
    </>
  );
};

// Sortable Promo Card Component
const SortablePromoCard = ({ item, index, isFirstCard, cardSampleRef, isExpired, isDeleted, isActive, activeDropdown, activeMenuDropdown, dropdownRef, menuDropdownRefs, setActiveDropdown, setActiveMenuDropdown, handleStatusChange, handleEditClick, handleDeleteClick, deletedPromoCodeIds }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        if (isFirstCard && cardSampleRef) {
          cardSampleRef.current = node;
        }
      }}
      style={style}
      className="h-full"
    >
      <Card
        extra="w-full h-full pt-4 pb-1  px-4   md:pt-4 md:pb-4 md:px-4 shadow-sm hover:shadow-md transition-shadow !bg-[#192242] !rounded-xl"
      >
        <div className="flex flex-col h-full promo-card-content">
          {/* Drag Handle Area */}
          <div 
            className="cursor-move flex-1"
            {...attributes}
            {...listeners}
            style={{ touchAction: 'none' }}
          >
          {/* Main Content */}
          <div className=" space-y-3">
            {/* Promo Code with Edit Button and Dropdown */}
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-xl 3xl:text-2xl font-bold text-white/90" style={{ textTransform: 'none' }}>
                {item.code ? item.code.charAt(0).toUpperCase() + item.code.slice(1).toLowerCase() : "-"}
              </h3>

              {/* Action Buttons - Right Side */}
              <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                {activeDropdown === item.id && !isExpired && !isDeleted && (
                  <div
                    className={`absolute right-0 z-50 mt-1 ${isActive ? 'w-22' : 'w-[75px]'} h-8 rounded-md bg-[#0b1437] border border-gray-600 p-1 shadow-lg flex items-center justify-center`}
                    ref={dropdownRef}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isActive ? (
                      <button
                        className="block w-full rounded-md bg-gray-600 px-3.5 py-0.5 text-left text-sm font-bold text-white hover:bg-gray-700 transition-colors"
                        onClick={() => {
                          handleStatusChange(item.id, "inactive");
                        }}
                      >
                        Inactive
                      </button>
                    ) : (
                      <button
                        className="block w-full rounded-md bg-gray-600 px-2.5 py-0.5 text-left text-sm font-bold text-white hover:bg-gray-700 transition-colors"
                        onClick={() => {
                          handleStatusChange(item.id, "active");
                        }}
                      >
                        Active
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* 3-Dot Menu Dropdown */}
              <div
                className="relative"
                ref={(el) => {
                  if (el) {
                    menuDropdownRefs.current[item.id] = el;
                  } else {
                    delete menuDropdownRefs.current[item.id];
                  }
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveMenuDropdown(
                      activeMenuDropdown === item.id
                        ? null
                        : item.id
                    );
                  }}
                  className="flex items-center justify-center h-7 w-7 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                  title="Menu"
                >
                  <BsThreeDotsVertical className="h-4 w-4 text-white/90" />
                </button>

                {activeMenuDropdown === item.id && (
                  <div
                    className="absolute right-0 z-50 mt-1 w-28 rounded-md bg-[#0b1437] border border-gray-600 shadow-lg py-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isDeleted) {
                          handleEditClick(item);
                          setActiveMenuDropdown(null);
                        }
                      }}
                      disabled={isDeleted}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-white/90 hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <MdEdit className="h-4 w-4" />
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isDeleted) {
                          handleDeleteClick(item);
                        }
                      }}
                      disabled={isDeleted}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <MdDelete className="h-4 w-4" />
                      <span>Delete</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="my-2 h-px w-full bg-white/10"></div>

          {/* Row 1: Discount Value, Usage Limit, and End Date */}
          <div className="flex flex-col gap-2 [&>*:last-child]:-mb-2">
            {/* Discount Value */}
            <div>
              <div className="flex items-center justify-between">
                <span className="text-sm 3xl:text-base font-medium text-white/90">
                  Discount Value:
                </span>
                <span className="text-xs 3xl:text-sm font-medium text-white/90">
                  {item.discountValue || "-"}
                </span>
              </div>
            </div>

            {/* Usage Limit */}
            <div>
              <div className="flex items-center justify-between">
                <span className="text-sm 3xl:text-base font-medium text-white/90">
                  Max Redemptions:
                </span>
                <span className="text-xs 3xl:text-sm  font-medium text-white/90">
                  {item.maxUsers || "--"}
                </span>
              </div>
            </div>

            {/* End Date */}
            <div className="mb-0">
              <div className="flex items-center justify-between">
                <span className="text-sm 3xl:text-base font-medium text-white/90">
                  End Date:
                </span>
                <span className="text-xs 3xl:text-sm font-medium text-white/90">
                  {item.endDate
                    ? moment(item.endDate).format("MM/DD/YYYY, hh:mm A")
                    : "No Expiry"}
                </span>
              </div>
            </div>

            {/* Visibility */}
            <div className="mb-0">
              <div className="flex items-center justify-between">
                <span className="text-sm 3xl:text-base font-medium text-white/90">
                  Coupon Visibility:
                </span>
                <div className="flex items-center gap-1.5">
                  <div className="relative group">
                    <IoMdInformationCircleOutline className="h-3.5 w-3.5 text-gray-400 hover:text-white/90 cursor-help transition-colors flex-shrink-0" />
                    <div className="absolute top-full -right-14 md:left-1/2 md:-translate-x-1/2 md:right-auto mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 w-[280px] md:w-auto md:max-w-none md:whitespace-nowrap">
                      <div className="bg-navy-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
                        {item.visibleOnApp !== undefined ? (item.visibleOnApp ? "Visible to all users" : "This is a private promo code and is not visible to all users") : (item.visibility === "public" ? "Visible to all users" : "This is a private promo code and is not visible to all users")}
                      </div>
                    </div>
                  </div>
                  <span className={`text-xs 3xl:text-sm font-medium capitalize ${(item.visibleOnApp !== undefined ? item.visibleOnApp : item.visibility === "public")
                      ? "text-green-400"
                    : "text-gray-400"
                    }`}>
                    {item.visibleOnApp !== undefined ? (item.visibleOnApp ? "public" : "private") : (item.visibility || "public")}
                  </span>
                </div>
              </div>
            </div>

            {/* Info Message Section */}
            <div className="mt-auto">
              {!isActive && (
                <div className="mt-0.5 w-[101%] -pb-36 mb-0">
                  <div className="flex items-start gap-1.5  rounded-md   px-3 py-2 relative overflow-hidden">
                    <div className="absolute inset-0 bg-white/10 rounded-md"></div>
                    <div className="relative z-10 flex items-start gap-1.5 w-full">
                      <IoMdInformationCircleOutline className="h-3.5 w-3.5 text-white/90 flex-shrink-0 mt-0.1" />
                      <p className="text-xs font-medium text-white/90 leading-relaxed" style={{
                        letterSpacing: "1px",
                        lineHeight: "1.3",
                      }}>
                        {isDeleted
                          ? "Promo code permanently deleted."
                          : isExpired
                            ? "Promo code no longer valid."
                            : "Deactivated by admin."}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

const Promotion = () => {
  // Tab selection state
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("offer_pricing_active_tab") || "coin-packages";
  });
  
  const dropdownRef = useRef();
  const menuDropdownRefs = useRef({});
  const cardContainerRef = useRef(null);
  const headerRef = useRef(null);
  const cardSampleRef = useRef(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeMenuDropdown, setActiveMenuDropdown] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingRow, setDeletingRow] = useState(null);
  const [editingRow, setEditingRow] = useState(null);

  const [editFormData, setEditFormData] = useState({
    code: "",
    description: "",
    discountValue: "",
    discountType: "%", // "%" for percentage, "$" for flat amount
    maxUsers: "",
    endDate: "",
    showEndDate: false,
    visibleOnApp: true, // boolean for visibility on app
  });
  const [editFormErrors, setEditFormErrors] = useState({});
  const [addFormData, setAddFormData] = useState({
    code: "",
    description: "",
    discountValue: "",
    discountType: "%", // "%" for percentage, "$" for flat amount
    maxUsers: "",
    endDate: "",
    hasExpiry: false,
    visibleOnApp: true, // boolean for visibility on app
  });
  const [addFormErrors, setAddFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchText, setSearchText] = useState("");

  // Promo codes data from API (all data fetched at once)
  const [data, setData] = useState([]);

  // Track deleted promo code IDs to prevent reactivation
  const [deletedPromoCodeIds, setDeletedPromoCodeIds] = useState(new Set());

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before activating
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fixed pagination state - always show 12 cards per page
  const [cardsPerPage] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);
  const lastWindowSize = useRef({ width: 0, height: 0 });

  // Coin Packages state
  const [coinPackagesData, setCoinPackagesData] = useState([]);
  const [isLoadingCoinPackages, setIsLoadingCoinPackages] = useState(false);
  const [isCoinPackageEditModalOpen, setIsCoinPackageEditModalOpen] = useState(false);
  const [isCoinPackageAddModalOpen, setIsCoinPackageAddModalOpen] = useState(false);
  const [isCoinPackageDeleteModalOpen, setIsCoinPackageDeleteModalOpen] = useState(false);
  const [deletingCoinPackage, setDeletingCoinPackage] = useState(null);
  const [editingCoinPackage, setEditingCoinPackage] = useState(null);
  const [coinPackageEditFormData, setCoinPackageEditFormData] = useState({
    package_name: "",
    price: "",
    coins: "",
    description: "",
  });
  const [coinPackageEditFormErrors, setCoinPackageEditFormErrors] = useState({});
  const [coinPackageAddFormData, setCoinPackageAddFormData] = useState({
    package_name: "",
    price: "",
    coins: "",
    description: "",
  });
  const [coinPackageAddFormErrors, setCoinPackageAddFormErrors] = useState({});
  // Fixed pagination - always show 12 items per page
  const [coinPackageItemsPerPage] = useState(12);
  const [coinPackageCurrentPage, setCoinPackageCurrentPage] = useState(1);
  const [deletedCoinPackageIds, setDeletedCoinPackageIds] = useState(new Set());
  const coinPackageMenuDropdownRefs = useRef({});
  const coinPackageStatusDropdownRefs = useRef({});
  const [openCoinPackageMenuId, setOpenCoinPackageMenuId] = useState(null);
  const [activeCoinPackageDropdown, setActiveCoinPackageDropdown] = useState(null);
  const [isEditOrderModalOpen, setIsEditOrderModalOpen] = useState(false);
  const [reorderCoinPackages, setReorderCoinPackages] = useState([]);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  // Promo Code Edit Order state
  const [isPromoCodeEditOrderModalOpen, setIsPromoCodeEditOrderModalOpen] = useState(false);
  const [reorderPromoCodes, setReorderPromoCodes] = useState([]);
  const [isSavingPromoCodeOrder, setIsSavingPromoCodeOrder] = useState(false);

  // Pricing Reference state
  const [isEditingReference, setIsEditingReference] = useState(false);
  const [isLoadingReference, setIsLoadingReference] = useState(false);
  const [isFetchingReference, setIsFetchingReference] = useState(false);
  const [referenceSettings, setReferenceSettings] = useState(null);
  const [rewardAmount, setRewardAmount] = useState("50");
  const [coinsForReferrer, setCoinsForReferrer] = useState("500");
  const [coinsForReferee, setCoinsForReferee] = useState("50");

  const [isReferenceActive, setIsReferenceActive] = useState(true);

  // Theme Plan state
  const [isEditingThemePlan, setIsEditingThemePlan] = useState(false);
  const [isAddingThemePlan, setIsAddingThemePlan] = useState(false);
  const [isLoadingThemePlan, setIsLoadingThemePlan] = useState(false);
  const [isFetchingThemePlan, setIsFetchingThemePlan] = useState(false);
  const [themePlanSettings, setThemePlanSettings] = useState(null);
  const [themePlanDiscount, setThemePlanDiscount] = useState("0");
  const [themePlanDiscountError, setThemePlanDiscountError] = useState("");
  const [themePlanCoinsPerMonth, setThemePlanCoinsPerMonth] = useState("0");
  const [themePlanDescription, setThemePlanDescription] = useState("Dark theme one-time purchase pricing");
  const [themePlanIsActive, setThemePlanIsActive] = useState(true);

  // Emoji Plan state
  const [isEditingEmojiPlan, setIsEditingEmojiPlan] = useState(false);
  const [isLoadingEmojiPlan, setIsLoadingEmojiPlan] = useState(false);
  const [isFetchingEmojiPlan, setIsFetchingEmojiPlan] = useState(false);
  const [emojiPlanDiscount, setEmojiPlanDiscount] = useState("0.00");
  const [emojiPlanDiscountError, setEmojiPlanDiscountError] = useState("");
  const [emojiPlanCoins, setEmojiPlanCoins] = useState("");
  const [emojiPlanSettings, setEmojiPlanSettings] = useState(null);

  // Verified Badge Plan state
  const [isEditingVerifiedBadge, setIsEditingVerifiedBadge] = useState(false);
  const [isLoadingVerifiedBadge, setIsLoadingVerifiedBadge] = useState(false);
  const [isFetchingVerifiedBadge, setIsFetchingVerifiedBadge] = useState(false);
  const [verifiedBadgeSettings, setVerifiedBadgeSettings] = useState(null);
  const [verifiedBadgeDiscount, setVerifiedBadgeDiscount] = useState("0");
  const [verifiedBadgeDiscountError, setVerifiedBadgeDiscountError] = useState("");
  const [verifiedBadgeCoins, setVerifiedBadgeCoins] = useState("0");

  const handleThemePlanDoubleClick = () => {
    setIsEditingThemePlan(true);
  };
  
  const handleReferenceDoubleClick = () => {
    setIsEditingReference(true);
  };
  const handleVerifiedBadgeDoubleClick = () => {
    setIsEditingVerifiedBadge(true);
  };
  const handleEmojiDoubleClick = () => {
    setIsEditingEmojiPlan(true);
  };
      

  
  
  useEffect(() => {
    if (activeTab) {
      localStorage.setItem("offer_pricing_active_tab", activeTab);
    }
  }, [activeTab]);
  




  // Fixed pagination - always 12 cards per page (dynamic calculation removed)

  // Fetch promo codes list on component mount (fetch all data for client-side pagination)
  useEffect(() => {
    if (activeTab === "promo-code") {
      // Check cache first
      const cache = tabCache["promo-code"];

      // If cached data exists, show it instantly
      if (cache.data) {
        setData(cache.data);
        setCurrentPage(1);
      }

      // Always fetch fresh data in background
      // Show loader only on firstLoad (when no cache exists yet)
      const shouldShowLoader = cache.firstLoad && !cache.data;
      fetchPromoCodesList(shouldShowLoader);
    }
  }, [searchText, activeTab]);

  // Fetch coin packages when tab is active
  useEffect(() => {
    if (activeTab === "coin-packages") {
      // Check cache first
      const cache = tabCache["coin-packages"];

      // If cached data exists, show it instantly
      if (cache.data) {
        setCoinPackagesData(cache.data);
        setCoinPackageCurrentPage(1);
      }

      // Always fetch fresh data in background
      // Show loader only on firstLoad (when no cache exists yet)
      const shouldShowLoader = cache.firstLoad && !cache.data;
      fetchCoinPackagesList(shouldShowLoader);
    }
  }, [activeTab]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
      // Check if click is outside any menu dropdown
      const clickedInsideMenu = Object.values(menuDropdownRefs.current).some(
        (ref) => ref && ref.contains(event.target)
      );
      if (!clickedInsideMenu) {
        setActiveMenuDropdown(null);
      }
      // Check if click is outside any coin package status dropdown
      const clickedInsideCoinPackageStatus = Object.values(coinPackageStatusDropdownRefs.current).some(
        (ref) => ref && ref.contains(event.target)
      );
      if (!clickedInsideCoinPackageStatus) {
        setActiveCoinPackageDropdown(null);
      }
      // Check if click is outside any coin package menu dropdown
      const clickedInsideCoinPackageMenu = Object.values(coinPackageMenuDropdownRefs.current).some(
        (ref) => ref && ref.contains(event.target)
      );
      if (!clickedInsideCoinPackageMenu) {
        setOpenCoinPackageMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Function to fetch promo codes list from API (fetch all data for client-side pagination)
  const fetchPromoCodesList = async (showLoader = true) => {
    if (showLoader) {
      setIsLoadingList(true);
    }
    try {
      // Fetch a large batch to enable client-side pagination
      const payload = {
        pagination: {
          page: 1,
          pageSize: 1000, // Large page size to get all data
          sort: {
            display_order: 1, // Sort by display_order field ascending
            createdAt: -1, // Fallback to createdAt if display_order is same
          },
          filters: {},
        },
        searchtext: searchText,
      };

      const response = await PromotionService.listPromoCodes(payload);

      if (response.data && response.data.data) {
        // Transform API response to match component's data structure
        const transformedData = response.data.data.map((item, index) => {
          // Format discount value based on discount_type
          let discountValue = "";
          if (item.discount_type === "percentage") {
            discountValue = `${item.discount_value}%`;
          } else if (item.discount_type === "fixed_amount" || item.discount_type === "fixed") {
            discountValue = `$${item.discount_value}`;
          } else {
            discountValue = item.discount_value?.toString() || "";
          }

          const promoCodeId = item._id || item.id;
          const isDeleted = item.is_deleted || item.deleted || deletedPromoCodeIds.has(promoCodeId);

          return {
            id: promoCodeId,
            code: item.code || "",
            discountValue: discountValue,
            discountType: (item.discount_type === "percentage" ? "%" : "$"), // Store UI format (percentage or fixed_amount/fixed)
            discountValueRaw: item.discount_value, // Store raw numeric value
            maxUsers: item.max_uses || item.maxUsers || 0,
            startDate: item.start_date || item.startDate || "",
            endDate: item.end_date || item.endDate || "",
            status: item.is_active ? "active" : "inactive",
            description: item.description || "",
            visibleOnApp: item.visibility === "public", // convert "public"/"private" to boolean
            deleted: isDeleted,
            display_order: item.display_order !== undefined && item.display_order !== null ? item.display_order : index, // Include display_order field, default to index if not present
          };
        });

        // Sort by display_order field (ascending) to ensure correct order
        transformedData.sort((a, b) => {
          if (a.display_order !== undefined && b.display_order !== undefined) {
            return a.display_order - b.display_order;
          }
          return 0;
        });

        // Update cache
        tabCache["promo-code"].data = transformedData;

        // Set firstLoad to false after successful fetch
        if (tabCache["promo-code"].firstLoad) {
          tabCache["promo-code"].firstLoad = false;
        }

        setData(transformedData);
        // Reset to page 1 when data changes
        setCurrentPage(1);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching promo codes list:", error);
      Toast("error", error.response?.data?.message || "Failed to fetch promo codes list");
      setData([]);
    } finally {
      if (showLoader) {
        setIsLoadingList(false);
      }
    }
  };

  const handleStatusChange = async (rowId, newStatus) => {
    setActiveDropdown(null);

    // Prevent activating deleted promo codes
    if (newStatus === "active" && deletedPromoCodeIds.has(rowId)) {
      return;
    }

    // Also check if the promo code in data is marked as deleted
    const promoCode = data.find(item => item.id === rowId);
    if (newStatus === "active" && promoCode?.deleted) {
      return;
    }

    try {
      // Convert status string to boolean for API
      const isActive = newStatus === "active";

      // Prepare API payload
      const payload = {
        promo_code_id: rowId,
        is_active: isActive,
      };

      // Make API call to update status
      const response = await PromotionService.updatePromoCode(payload);

      if (response.data) {
        Toast("success", response.data.message || "Promo code status updated successfully");
        // Refresh the list after status change
        fetchPromoCodesList(false);
      }
    } catch (error) {
      console.error("Error updating promo code status:", error);
      Toast("error", error.response?.data?.message || "Failed to update promo code status");
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = data.findIndex((item) => item.id === active.id);
    const newIndex = data.findIndex((item) => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Update local state immediately for smooth UI
    const newData = arrayMove(data, oldIndex, newIndex);
    
    // Update display_order values for all items
    const updatedData = newData.map((item, index) => ({
      ...item,
      display_order: index,
    }));

    setData(updatedData);

    // Update cache
    tabCache["promo-code"].data = updatedData;

    // Update order for all promo codes using existing updatePromoCode API
    try {
      // Update each promo code's display_order field using the existing update API
      const updatePromises = updatedData.map((item, index) => {
        return PromotionService.updatePromoCode({
          promo_code_id: item.id,
          display_order: index,
        });
      });

      // Wait for all updates to complete
      await Promise.all(updatePromises);
      Toast("success", "Promo codes reordered successfully");
    } catch (error) {
      console.error("Error reordering promo codes:", error);
      Toast("error", error.response?.data?.message || "Failed to reorder promo codes");
      // Revert to original data on error
      fetchPromoCodesList(false);
    }
  };

  const handleEditClick = (row) => {
    setEditingRow(row);
    // Show End Date toggle if endDate exists, otherwise default to false
    const hasEndDate = !!row.endDate;
    // Convert date string to datetime-local format (add time if not present)
    let endDateValue = "";
    if (row.endDate) {
      // If date includes time, use as is; otherwise add default time 12:00 AM
      if (row.endDate.includes("T")) {
        endDateValue = row.endDate.substring(0, 16); // Keep only date and time parts (YYYY-MM-DDTHH:mm)
      } else {
        endDateValue = `${row.endDate}T00:00`; // Add default time 12:00 AM
      }
    }
    // Extract numeric value from discountValue (remove % or $)
    const numericValue = row.discountValueRaw?.toString() || (row.discountValue ? row.discountValue.replace(/[%$]/g, "") : "");

    setEditFormData({
      code: row.code || "",
      description: row.description || "",
      discountValue: numericValue,
      discountType: row.discountType || "%", // Use stored discount type or default to %
      maxUsers: row.maxUsers || "",
      endDate: endDateValue,
      showEndDate: false,
      visibleOnApp: row.visibleOnApp !== undefined ? row.visibleOnApp : (row.visibility === "public"), // convert to boolean
    });
    setEditFormErrors({});
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setEditingRow(null);
    setEditFormData({
      code: "",
      description: "",
      discountValue: "",
      discountType: "%",
      maxUsers: "",
      endDate: "",
      showEndDate: false,
      visibleOnApp: true,
    });
    setEditFormErrors({});
  };

  const handleAddCodeName = () => {
    setIsAddModalOpen(true);
    setAddFormData({
      code: "",
      description: "",
      discountValue: "",
      discountType: "%",
      maxUsers: "",
      endDate: "",
      hasExpiry: false,
      visibleOnApp: true,
    });
    setAddFormErrors({});
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setAddFormData({
      code: "",
      description: "",
      discountValue: "",
      discountType: "%",
      maxUsers: "",
      endDate: "",
      hasExpiry: false,
      visibleOnApp: true,
    });
    setAddFormErrors({});
    setIsLoading(false);
  };

  const validateAddForm = () => {
    const errors = {};

    if (!addFormData.code.trim()) {
      errors.code = "Promo code cannot be empty";
    }

    if (!addFormData.description.trim()) {
      errors.description = "Description cannot be empty";
    }

    if (!addFormData.discountValue.trim()) {
      errors.discountValue = "Discount value cannot be empty";
    } else {
      // Parse numeric value (remove any symbols)
      const discountStr = addFormData.discountValue.trim().replace(/[%$]/g, "").replace(/,/g, "");
      const numValue = parseFloat(discountStr);

      if (isNaN(numValue) || numValue <= 0) {
        errors.discountValue = "Discount value must be greater than 0";
      } else if (addFormData.discountType === "%") {
        // Validate percentage: must be between 1-100
        if (numValue < 1 || numValue > 100) {
          errors.discountValue = "Percentage must be between 1 and 100";
        }
      }
      // For "$" type, just need to be positive (already checked above)
    }

    if (!addFormData.maxUsers || addFormData.maxUsers === "") {
      errors.maxUsers = "Usage limit is required";
    } else if (isNaN(addFormData.maxUsers) || Number(addFormData.maxUsers) <= 0) {
      errors.maxUsers = "Usage limit must be a positive number";
    }

    // Only validate end date if expiry toggle is ON
    if (addFormData.hasExpiry) {
      if (!addFormData.endDate) {
        errors.endDate = "End date is required";
      } else {
        // Validate that end date is not in the past
        // Note: start_date is automatically set to current date/time by the backend
        const endDate = new Date(addFormData.endDate);
        const now = new Date();
        if (endDate < now) {
          errors.endDate = "End date must be in the future";
        }
      }
    }

    setAddFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddPromoCode = async () => {
    if (!validateAddForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Extract numeric value (remove any symbols)
      const discountStr = addFormData.discountValue.trim().replace(/[%$]/g, "").replace(/,/g, "");
      const discountValue = parseFloat(discountStr);

      // Map UI discount type to API format
      // UI uses "%" or "$", API expects "percentage" or "fixed_amount"
      const apiDiscountType = addFormData.discountType === "%" ? "percentage" : "fixed_amount";

      // Prepare API payload with proper data types
      // start_date is automatically set to current date/time (user doesn't select it)
      const payload = {
        code: addFormData.code.trim().toUpperCase(), // Convert to uppercase as promo codes are typically uppercase
        description: addFormData.description.trim(),
        discount_type: apiDiscountType,
        discount_value: addFormData.discountType === "%" ? Math.round(discountValue) : parseFloat(discountValue.toFixed(2)), // Round percentage to integer, keep 2 decimals for fixed
        max_uses: parseInt(addFormData.maxUsers, 10), // Ensure integer
        is_active: true,
        start_date: new Date().toISOString(), // Automatically set to current date/time
        visibility: addFormData.visibleOnApp ? "public" : "private", // convert boolean to "public"/"private"
        display_order: data.length, // Set display_order to current array length (adds new item at the end)
      };

      // Only include end_date if expiry is enabled and date is provided
      if (addFormData.hasExpiry && addFormData.endDate) {
        payload.end_date = new Date(addFormData.endDate).toISOString();
      }

      console.log("Sending payload:", payload); // Debug log

      // Make API call
      const response = await PromotionService.createPromoCode(payload);

      if (response.data) {
        Toast("success", response.data.message || "Promo code created successfully");
        handleCloseAddModal();
        // Refresh the list after creating a new promo code
        fetchPromoCodesList(false);
      }
    } catch (error) {
      console.error("Error creating promo code:", error);
      console.error("Error response:", error.response);
      console.error("Error response data:", error.response?.data);

      // Handle validation errors - check for different error response formats
      let errorMessage = "Failed to create promo code. Please check your input and try again.";

      if (error.response?.data) {
        const errorData = error.response.data;

        // Check for validation errors object
        if (errorData.errors && typeof errorData.errors === 'object') {
          // Extract validation errors and format them
          const validationErrors = Object.entries(errorData.errors)
            .map(([field, message]) => `${field}: ${Array.isArray(message) ? message.join(', ') : message}`)
            .join('; ');
          errorMessage = `Validation failed: ${validationErrors}`;
        }
        // Check for validation array
        else if (Array.isArray(errorData.errors)) {
          errorMessage = `Validation failed: ${errorData.errors.join(', ')}`;
        }
        // Check for validation message
        else if (errorData.validation) {
          errorMessage = `Validation failed: ${errorData.validation}`;
        }
        // Check for error message
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
        // Check for error string
        else if (errorData.error) {
          errorMessage = errorData.error;
        }
      }

      Toast("error", errorMessage);
      setIsLoading(false);
    }
  };

  const validateEditForm = () => {
    const errors = {};

    if (!editFormData.code.trim()) {
      errors.code = "Promo code is required";
    }

    if (!editFormData.description.trim()) {
      errors.description = "Description is required";
    }

    if (!editFormData.discountValue.trim()) {
      errors.discountValue = "Discount value cannot be empty";
    } else {
      // Parse numeric value (remove any symbols)
      const discountStr = editFormData.discountValue.trim().replace(/[%$]/g, "").replace(/,/g, "");
      const numValue = parseFloat(discountStr);

      if (isNaN(numValue) || numValue <= 0) {
        errors.discountValue = "Discount value must be greater than 0";
      } else if (editFormData.discountType === "%") {
        // Validate percentage: must be between 1-100
        if (numValue < 1 || numValue > 100) {
          errors.discountValue = "Percentage must be between 1 and 100";
        }
      }
      // For "$" type, just need to be positive (already checked above)
    }

    if (!editFormData.maxUsers || editFormData.maxUsers === "") {
      errors.maxUsers = "Usage limit is required";
    } else if (isNaN(editFormData.maxUsers) || Number(editFormData.maxUsers) <= 0) {
      errors.maxUsers = "Usage limit must be a positive number";
    }

    // Only validate endDate if the toggle is ON
    if (editFormData.showEndDate && !editFormData.endDate) {
      errors.endDate = "End date is required";
    } else if (editFormData.showEndDate && editFormData.endDate && editingRow) {
      // Check if end date is before start date
      const startDate = editingRow.startDate;
      if (startDate) {
        // Convert startDate to datetime-local format for comparison
        let startDateValue = "";
        if (startDate.includes("T")) {
          startDateValue = startDate.substring(0, 16);
        } else {
          startDateValue = `${startDate}T00:00`;
        }

        if (new Date(editFormData.endDate) < new Date(startDateValue)) {
          errors.endDate = "End date cannot be before start date";
        }
      }
    }

    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveChanges = async () => {
    if (!validateEditForm()) {
      return;
    }

    if (editingRow) {
      setIsLoading(true);

      try {
        // Extract numeric value (remove any symbols)
        const discountStr = editFormData.discountValue.trim().replace(/[%$]/g, "").replace(/,/g, "");
        const discountValue = parseFloat(discountStr);

        // Map UI discount type to API format
        // UI uses "%" or "$", API expects "percentage" or "fixed_amount"
        const apiDiscountType = editFormData.discountType === "%" ? "percentage" : "fixed_amount";

        // Prepare API payload with proper data types
        const payload = {
          promo_code_id: editingRow.id,
          description: editFormData.description.trim(),
          discount_type: apiDiscountType,
          discount_value: editFormData.discountType === "%" ? Math.round(discountValue) : parseFloat(discountValue.toFixed(2)), // Round percentage to integer, keep 2 decimals for fixed
          max_uses: parseInt(editFormData.maxUsers, 10), // Ensure integer
          visibility: editFormData.visibleOnApp ? "public" : "private", // convert boolean to "public"/"private"
        };

        // Only include code if it has changed from the original value
        // This prevents "code already exists" error when updating other fields
        // Normalize both to uppercase for comparison (matching how codes are created)
        const trimmedCode = (editFormData.code || "").trim().toUpperCase();
        const originalCode = (editingRow.code || "").trim().toUpperCase();

        // Only include code in payload if it has actually changed
        // This is critical to prevent backend from checking for duplicates when only other fields are updated
        if (trimmedCode && trimmedCode !== originalCode) {
          payload.code = trimmedCode;
        }
        // Explicitly do NOT include code field if it hasn't changed or is empty

        // Only include end_date if showEndDate is true and endDate is provided
        if (editFormData.showEndDate && editFormData.endDate) {
          // Convert date to ISO format
          const endDateISO = new Date(editFormData.endDate).toISOString();
          payload.end_date = endDateISO;
        }
        // If toggle is off, don't include end_date in payload (let API handle it)

        console.log("Sending update payload:", payload); // Debug log

        // Make API call
        const response = await PromotionService.updatePromoCode(payload);

        if (response.data) {
          Toast("success", response.data.message || "Promo code updated successfully");
          setIsLoading(false);
          handleCloseModal();
          // Refresh the list after updating
          fetchPromoCodesList(false);
        }
      } catch (error) {
        console.error("Error updating promo code:", error);
        console.error("Error response:", error.response);
        console.error("Error response data:", error.response?.data);

        // Handle validation errors - check for different error response formats
        let errorMessage = "Failed to update promo code. Please check your input and try again.";

        if (error.response?.data) {
          const errorData = error.response.data;

          // Check for validation errors object
          if (errorData.errors && typeof errorData.errors === 'object') {
            // Extract validation errors and format them
            const validationErrors = Object.entries(errorData.errors)
              .map(([field, message]) => `${field}: ${Array.isArray(message) ? message.join(', ') : message}`)
              .join('; ');
            errorMessage = `Validation failed: ${validationErrors}`;
          }
          // Check for validation array
          else if (Array.isArray(errorData.errors)) {
            errorMessage = `Validation failed: ${errorData.errors.join(', ')}`;
          }
          // Check for validation message
          else if (errorData.validation) {
            errorMessage = `Validation failed: ${errorData.validation}`;
          }
          // Check for error message
          else if (errorData.message) {
            errorMessage = errorData.message;
          }
          // Check for error string
          else if (errorData.error) {
            errorMessage = errorData.error;
          }
        }

        Toast("error", errorMessage);
        setIsLoading(false);
      }
    }
  };

  // Helper function to check if a promo code is expired
  const isPromoExpired = (endDate) => {
    if (!endDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDateObj = new Date(endDate);
    endDateObj.setHours(0, 0, 0, 0);
    return endDateObj < today;
  };

  // Helper function to get the effective status (expired promo codes are always inactive)
  const getEffectiveStatus = (item) => {
    const isExpired = isPromoExpired(item.endDate);
    return isExpired ? false : item.status === "active";
  };

  // Delete handler
  const handleDeleteClick = (row) => {
    setDeletingRow(row);
    setIsDeleteModalOpen(true);
    setActiveMenuDropdown(null);
  };

  const handleDeleteConfirm = async () => {
    if (deletingRow) {
      setIsDeleting(true);
      try {
        const promoCodeId = deletingRow.id;

        // First, permanently disable the promo code by setting is_active to false
        try {
          const disablePayload = {
            promo_code_id: promoCodeId,
            is_active: false,
          };
          await PromotionService.updatePromoCode(disablePayload);
        } catch (disableError) {
          console.error("Error disabling promo code:", disableError);
          // Continue with delete even if disable fails
        }

        // Then delete the promo code
        const payload = {
          promo_code_id: promoCodeId,
        };

        const response = await PromotionService.deletePromoCode(payload);

        if (response.data) {
          // Mark this promo code as deleted to prevent reactivation
          setDeletedPromoCodeIds(prev => new Set([...prev, promoCodeId]));

          setIsDeleteModalOpen(false);
          setDeletingRow(null);
          // Refresh the list after delete
          fetchPromoCodesList(false);
        }
      } catch (error) {
        console.error("Error deleting promo code:", error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setDeletingRow(null);
  };

  // Pagination handlers
  const totalPages = Math.ceil(data.length / cardsPerPage);
  const startIndex = (currentPage - 1) * cardsPerPage;
  const endIndex = startIndex + cardsPerPage;
  const visiblePromoCards = data.slice(startIndex, endIndex);
  const totalCount = data.length;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      // Scroll to top when changing pages
      window.scrollTo(0, 0);
    }
  };

  // ========== Coin Packages Functions ==========
  const calculateCoins = (price) => {
    if (price === 10) return 100;
    if (price === 20) return 250;
    return price * 10;
  };

  const fetchCoinPackagesList = async (showLoader = true) => {
    if (showLoader) {
      setIsLoadingCoinPackages(true);
    }
    try {
      const payload = {
        pagination: {
          page: 1,
          pageSize: 1000,
          sort: {
            display_order: 1,
            price: 1,
          },
          filters: {},
        },
        searchtext: "",
      };
      const response = await CoinPackagesService.listCoinPackages(payload);

      if (response && response.data) {
        const responseData = response.data;
        const dataArray = responseData.data || responseData || [];

        const transformedData = Array.isArray(dataArray) ? dataArray.map((item, index) => {
          const packageId = item._id || item.id || item.package_id;
          const isDeleted = item.is_deleted || item.deleted || deletedCoinPackageIds.has(packageId);

          return {
            ...item,
            id: packageId,
            status: item.is_active ? "active" : "inactive",
            deleted: isDeleted,
            display_order: item.display_order !== undefined && item.display_order !== null ? item.display_order : index, // Include display_order field, default to index if not present
          };
        }) : [];

        // Sort by display_order field (ascending) to ensure correct order
        transformedData.sort((a, b) => {
          if (a.display_order !== undefined && b.display_order !== undefined) {
            return a.display_order - b.display_order;
          }
          return 0;
        });

        // Update cache
        tabCache["coin-packages"].data = transformedData;

        // Set firstLoad to false after successful fetch
        if (tabCache["coin-packages"].firstLoad) {
          tabCache["coin-packages"].firstLoad = false;
        }

        setCoinPackagesData(transformedData);
        setCoinPackageCurrentPage(1);
      } else {
        setCoinPackagesData([]);
      }
    } catch (error) {
      console.error("Error fetching coin packages list:", error);
      Toast("error", error.response?.data?.message || "Failed to fetch coin packages list");
      setCoinPackagesData([]);
    } finally {
      if (showLoader) {
        setIsLoadingCoinPackages(false);
      }
    }
  };

  // Handler for opening Edit Order modal
  const handleEditOrderClick = () => {
    // Create a copy of all coin packages for reordering (not just visible ones)
    setReorderCoinPackages([...coinPackagesData]);
    setIsEditOrderModalOpen(true);
  };

  // Handler for closing Edit Order modal
  const handleCloseEditOrderModal = () => {
    setIsEditOrderModalOpen(false);
    setReorderCoinPackages([]);
  };

  // Handler for opening Promo Code Edit Order modal
  const handlePromoCodeEditOrderClick = () => {
    // Create a copy of all promo codes for reordering (not just visible ones)
    setReorderPromoCodes([...data]);
    setIsPromoCodeEditOrderModalOpen(true);
  };

  // Handler for closing Promo Code Edit Order modal
  const handleClosePromoCodeEditOrderModal = () => {
    setIsPromoCodeEditOrderModalOpen(false);
    setReorderPromoCodes([]);
  };

  // State for tracking drag over item
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);

  // Handler for drag start in Edit Order modal
  const handleReorderDragStart = (event) => {
    setActiveId(event.active.id);
  };

  // Handler for drag over in Edit Order modal
  const handleReorderDragOver = (event) => {
    setOverId(event.over?.id || null);
  };

  // Handler for drag end in Edit Order modal
  const handleReorderDragEnd = (event) => {
    const { active, over } = event;

    setActiveId(null);
    setOverId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = reorderCoinPackages.findIndex((item) => item.id === active.id);
    const newIndex = reorderCoinPackages.findIndex((item) => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Update local state for reordering
    const newData = arrayMove(reorderCoinPackages, oldIndex, newIndex);
    setReorderCoinPackages(newData);
  };

  // Handler for drag end in Promo Code Edit Order modal
  const handlePromoCodeReorderDragEnd = (event) => {
    const { active, over } = event;

    setActiveId(null);
    setOverId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = reorderPromoCodes.findIndex((item) => item.id === active.id);
    const newIndex = reorderPromoCodes.findIndex((item) => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Update local state for reordering
    const newData = arrayMove(reorderPromoCodes, oldIndex, newIndex);
    setReorderPromoCodes(newData);
  };

  // Handler for saving the new order
  const handleSaveOrder = async () => {
    setIsSavingOrder(true);
    try {
      // Update display_order values for all items
      const updatedData = reorderCoinPackages.map((item, index) => ({
        ...item,
        display_order: index,
      }));

      // Update order for all coin packages using existing updateCoinPackage API
      const updatePromises = updatedData.map((item, index) => {
        return CoinPackagesService.updateCoinPackage({
          package_id: item.id,
          display_order: index,
        });
      });

      // Wait for all updates to complete
      await Promise.all(updatePromises);
      
      // Update main data and cache
      setCoinPackagesData(updatedData);
      tabCache["coin-packages"].data = updatedData;
      
      Toast("success", "Coin packages reordered successfully");
      handleCloseEditOrderModal();
    } catch (error) {
      console.error("Error reordering coin packages:", error);
      Toast("error", error.response?.data?.message || "Failed to reorder coin packages");
    } finally {
      setIsSavingOrder(false);
    }
  };

  // Handler for saving the new promo code order
  const handleSavePromoCodeOrder = async () => {
    setIsSavingPromoCodeOrder(true);
    try {
      // Update display_order values for all items
      const updatedData = reorderPromoCodes.map((item, index) => ({
        ...item,
        display_order: index,
      }));

      // Update order for all promo codes using existing updatePromoCode API
      const updatePromises = updatedData.map((item, index) => {
        return PromotionService.updatePromoCode({
          promo_code_id: item.id,
          display_order: index,
        });
      });

      // Wait for all updates to complete
      await Promise.all(updatePromises);
      
      // Update main data and cache
      setData(updatedData);
      tabCache["promo-code"].data = updatedData;
      
      Toast("success", "Promo codes reordered successfully");
      handleClosePromoCodeEditOrderModal();
    } catch (error) {
      console.error("Error reordering promo codes:", error);
      Toast("error", error.response?.data?.message || "Failed to reorder promo codes");
    } finally {
      setIsSavingPromoCodeOrder(false);
    }
  };

  const handleCoinPackageStatusChange = async (rowId, newStatus) => {
    setActiveCoinPackageDropdown(null);

    if (newStatus === "active" && deletedCoinPackageIds.has(rowId)) {
      return;
    }

    const packageItem = coinPackagesData.find(item => item.id === rowId);
    if (newStatus === "active" && packageItem?.deleted) {
      return;
    }

    try {
      const isActive = newStatus === "active";
      const payload = {
        package_id: rowId,
        is_active: isActive,
      };

      const response = await CoinPackagesService.updateCoinPackage(payload);

      if (response.data) {
        fetchCoinPackagesList(false);
      }
    } catch (error) {
      console.error("Error updating coin package status:", error);
    }
  };

  const handleCoinPackageEditClick = (row) => {
    setEditingCoinPackage(row);
    setCoinPackageEditFormData({
      package_name: row.package_name || row.packageName || "",
      price: row.price || "",
      coins: row.coins || "",
      description: row.description || "",
    });
    setCoinPackageEditFormErrors({});
    setIsCoinPackageEditModalOpen(true);
  };

  const handleCoinPackageCloseModal = () => {
    setIsCoinPackageEditModalOpen(false);
    setEditingCoinPackage(null);
    setCoinPackageEditFormData({
      package_name: "",
      price: "",
      coins: "",
      description: "",
    });
    setCoinPackageEditFormErrors({});
  };

  const handleAddCoinPackage = () => {
    setIsCoinPackageAddModalOpen(true);
    setCoinPackageAddFormData({
      package_name: "",
      price: "",
      coins: "",
      description: "",
    });
    setCoinPackageAddFormErrors({});
  };

  const handleCoinPackageCloseAddModal = () => {
    setIsCoinPackageAddModalOpen(false);
    setCoinPackageAddFormData({
      package_name: "",
      price: "",
      coins: "",
      description: "",
    });
    setCoinPackageAddFormErrors({});
    setIsLoading(false);
  };

  const validateCoinPackageAddForm = () => {
    const errors = {};

    if (!coinPackageAddFormData.package_name.trim()) {
      errors.package_name = "Package name cannot be empty";
    }

    if (!coinPackageAddFormData.price || coinPackageAddFormData.price === "") {
      errors.price = "Price is required";
    } else {
      const numValue = parseFloat(coinPackageAddFormData.price);
      if (isNaN(numValue) || numValue <= 0) {
        errors.price = "Price must be greater than 0";
      } else {
        // Check if price has more than 6 digits (counting only whole number part)
        const priceStr = coinPackageAddFormData.price.toString();
        const wholeNumberPart = priceStr.split('.')[0];
        if (wholeNumberPart.length > 6) {
          errors.price = "Price cannot have more than 6 digits";
        }
      }
    }

    if (!coinPackageAddFormData.coins || coinPackageAddFormData.coins === "") {
      errors.coins = "Coins is required";
    } else if (isNaN(coinPackageAddFormData.coins) || Number(coinPackageAddFormData.coins) <= 0) {
      errors.coins = "Coins must be a positive number";
    } else {
      // Check if coins has more than 6 digits
      const coinsStr = coinPackageAddFormData.coins.toString();
      if (coinsStr.length > 6) {
        errors.coins = "Coins cannot have more than 6 digits";
      }
    }


    if (!coinPackageAddFormData.description.trim()) {
      errors.description = "Description cannot be empty";
    }

    setCoinPackageAddFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddCoinPackageSubmit = async () => {
    if (!validateCoinPackageAddForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        package_name: coinPackageAddFormData.package_name.trim(),
        price: parseFloat(coinPackageAddFormData.price),
        coins: Number(coinPackageAddFormData.coins),
        currency: "USD",
        description: coinPackageAddFormData.description.trim(),
      };

      const response = await CoinPackagesService.createCoinPackage(payload);

      await fetchCoinPackagesList(false);

      handleCoinPackageCloseAddModal();
    } catch (error) {
      console.error("Error creating coin package:", error);
      Toast("error", error.response?.data?.message || "Failed to create coin package");
    } finally {
      setIsLoading(false);
    }
  };

  const validateCoinPackageEditForm = () => {
    const errors = {};

    if (!coinPackageEditFormData.package_name.trim()) {
      errors.package_name = "Package name cannot be empty";
    }

    if (!coinPackageEditFormData.price || coinPackageEditFormData.price === "") {
      errors.price = "Price is required";
    } else {
      const numValue = parseFloat(coinPackageEditFormData.price);
      if (isNaN(numValue) || numValue <= 0) {
        errors.price = "Price must be greater than 0";
      } else {
        // Check if price has more than 6 digits (counting only whole number part)
        const priceStr = coinPackageEditFormData.price.toString();
        const wholeNumberPart = priceStr.split('.')[0];
        if (wholeNumberPart.length > 6) {
          errors.price = "Price cannot have more than 6 digits";
        }
      }
    }

    if (!coinPackageEditFormData.coins || coinPackageEditFormData.coins === "") {
      errors.coins = "Coins is required";
    } else if (isNaN(coinPackageEditFormData.coins) || Number(coinPackageEditFormData.coins) <= 0) {
      errors.coins = "Coins must be a positive number";
    } else {
      // Check if coins has more than 6 digits
      const coinsStr = coinPackageEditFormData.coins.toString();
      if (coinsStr.length > 6) {
        errors.coins = "Coins cannot have more than 6 digits";
      }
    }


    if (!coinPackageEditFormData.description.trim()) {
      errors.description = "Description cannot be empty";
    }

    setCoinPackageEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCoinPackageSaveChanges = async () => {
    if (!validateCoinPackageEditForm()) {
      return;
    }

    if (editingCoinPackage) {
      setIsLoading(true);

      try {
        const packageId = editingCoinPackage.id || editingCoinPackage._id || editingCoinPackage.package_id;

        if (!packageId) {
          Toast("error", "Package ID not found");
          setIsLoading(false);
          return;
        }

        const payload = {
          package_id: packageId,
          package_name: coinPackageEditFormData.package_name.trim(),
          price: parseFloat(coinPackageEditFormData.price),
          coins: Number(coinPackageEditFormData.coins),
          description: coinPackageEditFormData.description.trim(),
          is_popular: editingCoinPackage.is_popular !== undefined ? editingCoinPackage.is_popular : false,
        };

        const response = await CoinPackagesService.updateCoinPackage(payload);

        await fetchCoinPackagesList(false);

        handleCoinPackageCloseModal();
      } catch (error) {
        console.error("Error updating coin package:", error);
        Toast("error", error.response?.data?.message || "Failed to update coin package");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCoinPackageDeleteClick = (row) => {
    setDeletingCoinPackage(row);
    setIsCoinPackageDeleteModalOpen(true);
    setOpenCoinPackageMenuId(null);
  };

  const handleCoinPackageDeleteConfirm = async () => {
    if (deletingCoinPackage) {
      setIsDeleting(true);
      try {
        const packageId = deletingCoinPackage.id || deletingCoinPackage._id || deletingCoinPackage.package_id;

        if (!packageId) {
          Toast("error", "Package ID not found");
          setIsDeleting(false);
          return;
        }

        const payload = {
          package_id: packageId,
        };

        const response = await CoinPackagesService.deleteCoinPackage(payload);

        if (response.data) {
          setIsCoinPackageDeleteModalOpen(false);
          setDeletingCoinPackage(null);
          await fetchCoinPackagesList(false);
        }
      } catch (error) {
        console.error("Error deleting coin package:", error);
        Toast("error", error.response?.data?.message || "Failed to delete coin package");
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleCoinPackageDeleteCancel = () => {
    setIsCoinPackageDeleteModalOpen(false);
    setDeletingCoinPackage(null);
  };

  const getCoinPackageItemId = (item, index) => {
    return item.id || item._id || item.package_id || `item-${index}`;
  };

  const toggleCoinPackageMenu = (id) => {
    setOpenCoinPackageMenuId(openCoinPackageMenuId === id ? null : id);
  };

  // Coin Packages pagination
  const coinPackageTotalPages = Math.ceil(coinPackagesData.length / coinPackageItemsPerPage);
  const coinPackageStart = (coinPackageCurrentPage - 1) * coinPackageItemsPerPage;
  const visibleCoinPackages = coinPackagesData.slice(coinPackageStart, coinPackageStart + coinPackageItemsPerPage);
  const coinPackageTotalCount = coinPackagesData.length;

  const handleCoinPackagePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= coinPackageTotalPages) {
      setCoinPackageCurrentPage(newPage);
      window.scrollTo(0, 0);
    }
  };

  // ========== Pricing Reference Functions ==========
  // Fetch referral settings when pricing-reference tab is active
  useEffect(() => {
    if (activeTab === "pricing-reference") {
      const cache = tabCache["pricing-reference"];

      // If cached data exists, show it instantly
      if (cache.data?.referenceSettings) {
        const settingsData = cache.data.referenceSettings;
        setReferenceSettings(settingsData);
        setRewardAmount(settingsData.coins_for_referrer?.toString() || "50");
        setCoinsForReferrer(settingsData.coins_for_referrer?.toString() || "500");
        setCoinsForReferee(settingsData.coins_for_referee?.toString() || "50");

        setIsReferenceActive(settingsData.is_active !== undefined ? settingsData.is_active : true);
      }

      // If cached data exists, show it instantly
      if (cache.data?.themePlanSettings) {
        const settingsData = cache.data.themePlanSettings;
        setThemePlanSettings(settingsData);
        const discountValue = settingsData.discount_percentage || settingsData.discount || "0";
        setThemePlanDiscount(discountValue.toString());
        const coinsValue = settingsData.coins || settingsData.coins_per_month || "0";
        setThemePlanCoinsPerMonth(coinsValue.toString());
      }

      // If cached data exists, show it instantly
      if (cache.data?.emojiPlanSettings) {
        const settingsData = cache.data.emojiPlanSettings;
        setEmojiPlanSettings(settingsData);
        const discountValue = settingsData.discount_percentage || settingsData.discount || "0.00";
        setEmojiPlanDiscount(discountValue.toString());
        const coinsValue = settingsData.coins_per_month || settingsData.coins || "0";
        setEmojiPlanCoins(coinsValue.toString());
      }

      // If cached data exists, show it instantly
      if (cache.data?.verifiedBadgeSettings) {
        const settingsData = cache.data.verifiedBadgeSettings;
        setVerifiedBadgeSettings(settingsData);
        const discountValue = settingsData.discount_percentage || settingsData.discount || "0";
        setVerifiedBadgeDiscount(discountValue.toString());
        const coinsValue = settingsData.coins_per_month || settingsData.coins || "0";
        setVerifiedBadgeCoins(coinsValue.toString());
      }

      // Always fetch fresh data in background
      // Show loader only on firstLoad (when no cache exists yet)
      const shouldShowLoader = cache.firstLoad && !cache.data;

      if (!referenceSettings) {
        fetchReferenceSettings(shouldShowLoader);
      }
      if (!themePlanSettings) {
        fetchThemePlanSettings(shouldShowLoader);
      }
      if (!emojiPlanSettings) {
        fetchEmojiPlanSettings(shouldShowLoader);
      }
      if (!verifiedBadgeSettings) {
        fetchVerifiedBadgeSettings(shouldShowLoader);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchReferenceSettings = async (showLoader = true) => {
    if (showLoader) {
      setIsFetchingReference(true);
    }
    try {
      const response = await ReferralService.getReferralSettings();

      if (response.data && response.data.data) {
        const settingsData = response.data.data;

        // Update cache
        if (!tabCache["pricing-reference"].data) {
          tabCache["pricing-reference"].data = {};
        }
        tabCache["pricing-reference"].data.referenceSettings = settingsData;

        // Set firstLoad to false after successful fetch
        if (tabCache["pricing-reference"].firstLoad) {
          tabCache["pricing-reference"].firstLoad = false;
        }

        setReferenceSettings(settingsData);
        setRewardAmount(settingsData.coins_for_referrer?.toString() || "50");
        setCoinsForReferrer(settingsData.coins_for_referrer?.toString() || "500");
        setCoinsForReferee(settingsData.coins_for_referee?.toString() || "50");
        setIsReferenceActive(settingsData.is_active !== undefined ? settingsData.is_active : true);
      } else {
        // No settings found, create default settings (only once)
        await createDefaultReferenceSettings();
      }
    } catch (error) {
      // If settings don't exist (404 or empty), create default settings
      if (error.response?.status === 404 || !error.response?.data?.data) {
        await createDefaultReferenceSettings();
      } else {
        Toast("error", error.response?.data?.message || "Failed to fetch referral settings");
      }
    } finally {
      if (showLoader) {
        setIsFetchingReference(false);
      }
    }
  };

  const createDefaultReferenceSettings = async () => {
    try {
      const defaultData = {
        coins_for_referrer: 50,
        is_active: true,
        description: "Refer a friend and earn coins!",
      };

      const response = await ReferralService.createReferralSettings(defaultData);

      if (response.data && response.data.data) {
        const settingsData = response.data.data;
        setReferenceSettings(settingsData);
        setRewardAmount(settingsData.coins_for_referrer?.toString() || "50");
        setIsReferenceActive(settingsData.is_active !== undefined ? settingsData.is_active : true);
        Toast("success", "Referral settings created successfully");
      }
    } catch (error) {
      Toast("error", error.response?.data?.message || "Failed to create referral settings");
    }
  };

  const handleReferenceEditClick = () => {
    setIsEditingReference(true);
  };

  const handleReferenceSave = async () => {
    if (!referenceSettings || !referenceSettings._id) {
      Toast("error", "Settings ID not found");
      return;
    }

    try {
      setIsLoadingReference(true);
      const updateData = {
        settings_id: referenceSettings._id,
        coins_for_referrer: parseInt(coinsForReferrer) || 50,
        coins_for_referee: parseInt(coinsForReferee) || 0,
        is_active: isReferenceActive,
      };

      const response = await ReferralService.updateReferralSettings(updateData);

      if (response.data && response.data.data) {
        const updatedSettings = response.data.data;
        setReferenceSettings(updatedSettings);
        setRewardAmount(updatedSettings.coins_for_referrer?.toString() || "50");
        setIsReferenceActive(updatedSettings.is_active !== undefined ? updatedSettings.is_active : true);
        setIsEditingReference(false);
        Toast("success", "Reference reward settings updated successfully");
      }
    } catch (error) {
      Toast("error", error.response?.data?.message || "Failed to update referral settings");
    } finally {
      setIsLoadingReference(false);
    }
  };

  const handleReferenceCancel = () => {
    if (referenceSettings) {
      setRewardAmount(referenceSettings.coins_for_referrer?.toString() || "50");
      setCoinsForReferrer(referenceSettings.coins_for_referrer?.toString() || "500");
      setCoinsForReferee(referenceSettings.coins_for_referee?.toString() || "50");
      setIsReferenceActive(referenceSettings.is_active !== undefined ? referenceSettings.is_active : true);
    }
    setIsEditingReference(false);
  };

  // Fetch theme plan settings
  const fetchThemePlanSettings = async (showLoader = true) => {
    if (showLoader) {
      setIsFetchingThemePlan(true);
    }
    try {
      const response = await DarkThemeService.getThemePlanSettings();

      if (response.data && response.data.data) {
        const settingsData = response.data.data;

        // Update cache
        if (!tabCache["pricing-reference"].data) {
          tabCache["pricing-reference"].data = {};
        }
        tabCache["pricing-reference"].data.themePlanSettings = settingsData;

        // Set firstLoad to false after successful fetch
        if (tabCache["pricing-reference"].firstLoad) {
          tabCache["pricing-reference"].firstLoad = false;
        }

        setThemePlanSettings(settingsData);
        // Handle both discount and discount_percentage field names
        const discountValue = settingsData.discount_percentage || settingsData.discount || "0";
        setThemePlanDiscount(discountValue.toString());
        // Handle both coins and coins_per_month field names
        const coinsValue = settingsData.coins || settingsData.coins_per_month || "0";
        setThemePlanCoinsPerMonth(coinsValue.toString());
      } else if (response.data) {
        // Handle case where data might be directly in response.data
        const settingsData = response.data;

        // Update cache
        if (!tabCache["pricing-reference"].data) {
          tabCache["pricing-reference"].data = {};
        }
        tabCache["pricing-reference"].data.themePlanSettings = settingsData;

        // Set firstLoad to false after successful fetch
        if (tabCache["pricing-reference"].firstLoad) {
          tabCache["pricing-reference"].firstLoad = false;
        }

        setThemePlanSettings(settingsData);
        // Handle both discount and discount_percentage field names
        const discountValue = settingsData.discount_percentage || settingsData.discount || "0";
        setThemePlanDiscount(discountValue.toString());
        // Handle both coins and coins_per_month field names
        const coinsValue = settingsData.coins || settingsData.coins_per_month || "0";
        setThemePlanCoinsPerMonth(coinsValue.toString());
      } else {
        // No settings found, don't auto-create - let user add manually
        setThemePlanSettings(null);
      }
    } catch (error) {
      console.error("Error fetching theme plan settings:", error);
      // If settings don't exist (404 or empty), don't auto-create - let user add manually
      if (error.response?.status === 404 || !error.response?.data?.data) {
        setThemePlanSettings(null);
      } else {
        Toast("error", error.response?.data?.message || "Failed to fetch theme plan settings");
      }
    } finally {
      if (showLoader) {
        setIsFetchingThemePlan(false);
      }
    }
  };

  // Create default theme plan settings
  const createDefaultThemePlanSettings = async () => {
    try {
      const defaultData = {
        coins_per_month: 200,
        discount_percentage: 10,
        description: "Dark theme subscription pricing",
        is_active: true,
      };

      const response = await DarkThemeService.createThemePlanSettings(defaultData);

      if (response.data && response.data.data) {
        const settingsData = response.data.data;
        setThemePlanSettings(settingsData);
        // Handle both discount and discount_percentage field names
        const discountValue = settingsData.discount_percentage || settingsData.discount || "10";
        setThemePlanDiscount(discountValue.toString());
        // Handle both coins and coins_per_month field names
        const coinsValue = settingsData.coins || settingsData.coins_per_month || "200";
        setThemePlanCoinsPerMonth(coinsValue.toString());
        Toast("success", "Theme plan settings created successfully");
      } else if (response.data) {
        // Handle case where data might be directly in response.data
        const settingsData = response.data;
        setThemePlanSettings(settingsData);
        const discountValue = settingsData.discount_percentage || settingsData.discount || "10";
        setThemePlanDiscount(discountValue.toString());
        // Handle both coins and coins_per_month field names
        const coinsValue = settingsData.coins || settingsData.coins_per_month || "200";
        setThemePlanCoinsPerMonth(coinsValue.toString());
        Toast("success", "Theme plan settings created successfully");
      }
    } catch (error) {
      console.error("Error creating theme plan settings:", error);
      Toast("error", error.response?.data?.message || "Failed to create theme plan settings");
    }
  };

  // Theme Plan handlers
  const handleThemePlanAddClick = () => {
    setIsAddingThemePlan(true);
    setThemePlanDiscount("10");
    setThemePlanDiscountError("");
    setThemePlanCoinsPerMonth("3");
    setThemePlanDescription("Dark theme one-time purchase pricing");
    setThemePlanIsActive(true);
  };

  const handleThemePlanAddCancel = () => {
    setIsAddingThemePlan(false);
    setThemePlanDiscount("0");
    setThemePlanDiscountError("");
    setThemePlanCoinsPerMonth("0");
    setThemePlanDescription("Dark theme one-time purchase pricing");
    setThemePlanIsActive(true);
  };

  const handleThemePlanAdd = async () => {
    // Validate discount before saving
    const discountValue = parseFloat(themePlanDiscount);
    if (isNaN(discountValue) || discountValue < 0 || discountValue > 100) {
      setThemePlanDiscountError("Discount must be between 0 and 100");
      return;
    }

    try {
      setIsLoadingThemePlan(true);

      const createData = {
        coins: parseInt(themePlanCoinsPerMonth) || 0,
        discount_percentage: parseFloat(themePlanDiscount) || 0,
        description: themePlanDescription || "Dark theme one-time purchase pricing",
        is_active: themePlanIsActive,
      };

      const response = await DarkThemeService.createThemePlanSettings(createData);

      if (response.data) {
        // Update local state with response data if available
        if (response.data.data) {
          const settingsData = response.data.data;
          setThemePlanSettings(settingsData);
          // Handle both discount and discount_percentage field names
          const discountValue = settingsData.discount_percentage || settingsData.discount || themePlanDiscount;
          setThemePlanDiscount(discountValue.toString());
          // Handle both coins and coins_per_month field names
          const coinsValue = settingsData.coins || settingsData.coins_per_month || themePlanCoinsPerMonth;
          setThemePlanCoinsPerMonth(coinsValue.toString());
          setThemePlanDescription(settingsData.description || themePlanDescription);
          setThemePlanIsActive(settingsData.is_active !== undefined ? settingsData.is_active : true);
        } else if (response.data) {
          // Handle case where data might be directly in response.data
          const settingsData = response.data;
          setThemePlanSettings(settingsData);
          const discountValue = settingsData.discount_percentage || settingsData.discount || themePlanDiscount;
          setThemePlanDiscount(discountValue.toString());
          // Handle both coins and coins_per_month field names
          const coinsValue = settingsData.coins || settingsData.coins_per_month || themePlanCoinsPerMonth;
          setThemePlanCoinsPerMonth(coinsValue.toString());
          setThemePlanDescription(settingsData.description || themePlanDescription);
          setThemePlanIsActive(settingsData.is_active !== undefined ? settingsData.is_active : true);
        }
        setIsAddingThemePlan(false);
        Toast("success", response.data.message || "Theme Plan settings created successfully");
      }
    } catch (error) {
      console.error("Error creating theme plan settings:", error);
      Toast("error", error.response?.data?.message || "Failed to create theme plan settings");
    } finally {
      setIsLoadingThemePlan(false);
    }
  };

  const handleThemePlanEditClick = () => {
    setThemePlanDiscountError("");
    setIsEditingThemePlan(true);
  };

  const handleThemePlanSave = async () => {
    // Validate discount before saving
    const discountValue = parseFloat(themePlanDiscount);
    if (isNaN(discountValue) || discountValue < 0 || discountValue > 100) {
      setThemePlanDiscountError("Discount must be between 0 and 100");
      return;
    }

    try {
      setIsLoadingThemePlan(true);

      const updateData = {
        discount_percentage: parseFloat(themePlanDiscount) || 0,
        coins_per_month: parseInt(themePlanCoinsPerMonth) || 0,
        description: themePlanSettings?.description || "Dark theme subscription pricing",
      };

      const response = await DarkThemeService.updateThemePlanSettings(updateData);

      if (response.data) {
        // Update local state with response data if available
        if (response.data.data) {
          const updatedSettings = response.data.data;
          setThemePlanSettings(updatedSettings);
          // Handle both discount and discount_percentage field names
          const discountValue = updatedSettings.discount_percentage || updatedSettings.discount || themePlanDiscount;
          setThemePlanDiscount(discountValue.toString());
          // Handle both coins and coins_per_month field names
          const coinsValue = updatedSettings.coins || updatedSettings.coins_per_month || themePlanCoinsPerMonth;
          setThemePlanCoinsPerMonth(coinsValue.toString());
        } else if (response.data) {
          // Handle case where data might be directly in response.data
          const updatedSettings = response.data;
          setThemePlanSettings(updatedSettings);
          const discountValue = updatedSettings.discount_percentage || updatedSettings.discount || themePlanDiscount;
          setThemePlanDiscount(discountValue.toString());
          // Handle both coins and coins_per_month field names
          const coinsValue = updatedSettings.coins || updatedSettings.coins_per_month || themePlanCoinsPerMonth;
          setThemePlanCoinsPerMonth(coinsValue.toString());
        }
        setIsEditingThemePlan(false);
        Toast("success", response.data.message || "Theme Plan settings updated successfully");

        // Create emoji settings after dark theme save
        try {
          const emojiCreateData = {
            coins_per_month: 100,
            discount_percentage: 10,
            description: "Emoji subscription monthly pricing",
            is_active: true,
          };
          await EmojiService.createEmojiSettings(emojiCreateData);
        } catch (emojiError) {
          console.error("Error creating emoji settings:", emojiError);
          // Don't show error toast for emoji creation failure to avoid confusion
        }
      }
    } catch (error) {
      console.error("Error updating theme plan settings:", error);
      Toast("error", error.response?.data?.message || "Failed to update theme plan settings");
    } finally {
      setIsLoadingThemePlan(false);
    }
  };

  const handleThemePlanCancel = () => {
    // Reset to saved values from settings
    if (themePlanSettings) {
      // Handle both discount and discount_percentage field names
      const discountValue = themePlanSettings.discount_percentage || themePlanSettings.discount || "0";
      setThemePlanDiscount(discountValue.toString());
      // Handle both coins and coins_per_month field names
      const coinsValue = themePlanSettings.coins || themePlanSettings.coins_per_month || "0";
      setThemePlanCoinsPerMonth(coinsValue.toString());
    } else {
      setThemePlanDiscount("0");
      setThemePlanCoinsPerMonth("0");
    }
    setThemePlanDiscountError("");
    setIsEditingThemePlan(false);
  };

  // Fetch emoji plan settings
  const fetchEmojiPlanSettings = async (showLoader = true) => {
    if (showLoader) {
      setIsFetchingEmojiPlan(true);
    }
    try {
      const response = await EmojiService.getEmojiSettings();

      if (response.data && response.data.data) {
        const settingsData = response.data.data;

        // Update cache
        if (!tabCache["pricing-reference"].data) {
          tabCache["pricing-reference"].data = {};
        }
        tabCache["pricing-reference"].data.emojiPlanSettings = settingsData;

        // Set firstLoad to false after successful fetch
        if (tabCache["pricing-reference"].firstLoad) {
          tabCache["pricing-reference"].firstLoad = false;
        }

        setEmojiPlanSettings(settingsData);
        // Handle both discount and discount_percentage field names
        const discountValue = settingsData.discount_percentage || settingsData.discount || "0.00";
        setEmojiPlanDiscount(discountValue.toString());
        // Handle both coins and coins_per_month field names
        const coinsValue = settingsData.coins_per_month || settingsData.coins || "0";
        setEmojiPlanCoins(coinsValue.toString());
      } else if (response.data) {
        setEmojiPlanSettings(response.data);
      }
    } catch (error) {
      console.error("Error fetching emoji plan settings:", error);
      Toast("error", error.response?.data?.message || "Failed to fetch emoji plan settings");
    } finally {
      setIsFetchingEmojiPlan(false);
    }
  };

  // Emoji Plan handlers
  const handleEmojiPlanEditClick = () => {
    setEmojiPlanDiscountError("");
    setIsEditingEmojiPlan(true);
  };

  const handleEmojiPlanSave = async () => {
    // Validate discount before saving
    const discountValue = parseFloat(emojiPlanDiscount);
    if (isNaN(discountValue) || discountValue < 0 || discountValue > 100) {
      setEmojiPlanDiscountError("Discount must be between 0 and 100");
      return;
    }

    try {
      setIsLoadingEmojiPlan(true);

      const updateData = {
        coins_per_month: parseInt(emojiPlanCoins) || 100,
        discount_percentage: parseFloat(emojiPlanDiscount) || 10,
        description: "Emoji subscription monthly pricing",
        is_active: true,
      };

      const response = await EmojiService.createEmojiSettings(updateData);

      // Update local state with response data
      if (response.data && response.data.data) {
        setEmojiPlanSettings(response.data.data);
        // Update form fields with response values
        const discountValue = response.data.data.discount_percentage || response.data.data.discount || "0.00";
        setEmojiPlanDiscount(discountValue.toString());
        const coinsValue = response.data.data.coins_per_month || response.data.data.coins || "0";
        setEmojiPlanCoins(coinsValue.toString());
      } else {
        setEmojiPlanSettings(updateData);
      }

      setIsEditingEmojiPlan(false);
      Toast("success", "Emoji Plan settings updated successfully");
    } catch (error) {
      console.error("Error updating emoji plan settings:", error);
      Toast("error", error.response?.data?.message || "Failed to update emoji plan settings");
    } finally {
      setIsLoadingEmojiPlan(false);
    }
  };

  const handleEmojiPlanCancel = () => {
    // Reset to saved values from settings
    if (emojiPlanSettings) {
      const discountValue = emojiPlanSettings.discount_percentage || emojiPlanSettings.discount || "0.00";
      setEmojiPlanDiscount(discountValue.toString());
      // Handle both coins and coins_per_month field names
      const coinsValue = emojiPlanSettings.coins_per_month || emojiPlanSettings.coins || "0";
      setEmojiPlanCoins(coinsValue.toString());
    } else {
      setEmojiPlanDiscount("0.00");
      setEmojiPlanCoins("0");
    }
    setEmojiPlanDiscountError("");
    setIsEditingEmojiPlan(false);
  };

  // Fetch verified badge settings
  const fetchVerifiedBadgeSettings = async (showLoader = true) => {
    if (showLoader) {
      setIsFetchingVerifiedBadge(true);
    }
    try {
      const response = await VerificationBadgeService.getVerifiedBadgeSettings();

      // Handle different response structures (array or object)
      let settingsData = null;
      if (response.data) {
        if (response.data.data !== undefined) {
          // Handle response.data.data (could be array or object)
          settingsData = Array.isArray(response.data.data) 
            ? response.data.data[0] || null
            : response.data.data;
        } else {
          // Handle response.data directly (could be array or object)
          settingsData = Array.isArray(response.data) 
            ? response.data[0] || null
            : response.data;
        }
      }

      if (settingsData) {
        // Update cache
        if (!tabCache["pricing-reference"].data) {
          tabCache["pricing-reference"].data = {};
        }
        tabCache["pricing-reference"].data.verifiedBadgeSettings = settingsData;

        // Set firstLoad to false after successful fetch
        if (tabCache["pricing-reference"].firstLoad) {
          tabCache["pricing-reference"].firstLoad = false;
        }

        setVerifiedBadgeSettings(settingsData);
        // Handle both discount and discount_percentage field names
        const discountValue = settingsData.discount_percentage || settingsData.discount || "0";
        setVerifiedBadgeDiscount(discountValue.toString());
        // Handle both coins and coins_per_month field names
        const coinsValue = settingsData.coins_per_month || settingsData.coins || "0";
        setVerifiedBadgeCoins(coinsValue.toString());
      } else {
        // No settings found
        setVerifiedBadgeSettings(null);
      }
    } catch (error) {
      console.error("Error fetching verified badge settings:", error);
      // If settings don't exist (404 or empty), don't auto-create - let user add manually
      if (error.response?.status === 404 || !error.response?.data?.data) {
        setVerifiedBadgeSettings(null);
      } else {
        Toast("error", error.response?.data?.message || "Failed to fetch verified badge settings");
      }
    } finally {
      if (showLoader) {
        setIsFetchingVerifiedBadge(false);
      }
    }
  };

  // Verified Badge handlers
  const handleVerifiedBadgeEditClick = () => {
    setVerifiedBadgeDiscountError("");
    setIsEditingVerifiedBadge(true);
  };

  const handleVerifiedBadgeSave = async () => {
    // Validate discount before saving
    const discountValue = parseFloat(verifiedBadgeDiscount);
    if (isNaN(discountValue) || discountValue < 0 || discountValue > 100) {
      setVerifiedBadgeDiscountError("Discount must be between 0 and 100");
      return;
    }

    try {
      setIsLoadingVerifiedBadge(true);

      if (verifiedBadgeSettings && verifiedBadgeSettings._id) {
        // Update existing settings
        const updateData = {
          discount_percentage: parseFloat(verifiedBadgeDiscount) || 0,
          coins_per_month: parseInt(verifiedBadgeCoins) || 0,
          description: verifiedBadgeSettings?.description || "Verified badge subscription pricing",
          is_active: verifiedBadgeSettings?.is_active !== undefined ? verifiedBadgeSettings.is_active : true,
        };

        const response = await VerificationBadgeService.updateVerifiedBadgeSettings(
          verifiedBadgeSettings._id,
          updateData
        );

        // Update local state with response data
        if (response.data && response.data.data) {
          const updatedSettings = response.data.data;
          setVerifiedBadgeSettings(updatedSettings);
          // Handle both discount and discount_percentage field names
          const discountValue = updatedSettings.discount_percentage || updatedSettings.discount || verifiedBadgeDiscount;
          setVerifiedBadgeDiscount(discountValue.toString());
          // Handle both coins and coins_per_month field names
          const coinsValue = updatedSettings.coins_per_month || updatedSettings.coins || verifiedBadgeCoins;
          setVerifiedBadgeCoins(coinsValue.toString());

          // Update cache
          if (!tabCache["pricing-reference"].data) {
            tabCache["pricing-reference"].data = {};
          }
          tabCache["pricing-reference"].data.verifiedBadgeSettings = updatedSettings;
        } else if (response.data) {
          const updatedSettings = response.data;
          setVerifiedBadgeSettings(updatedSettings);
          const discountValue = updatedSettings.discount_percentage || updatedSettings.discount || verifiedBadgeDiscount;
          setVerifiedBadgeDiscount(discountValue.toString());
          const coinsValue = updatedSettings.coins_per_month || updatedSettings.coins || verifiedBadgeCoins;
          setVerifiedBadgeCoins(coinsValue.toString());

          // Update cache
          if (!tabCache["pricing-reference"].data) {
            tabCache["pricing-reference"].data = {};
          }
          tabCache["pricing-reference"].data.verifiedBadgeSettings = updatedSettings;
        }

        setIsEditingVerifiedBadge(false);
        Toast("success", response.data?.message || "Verified badge settings updated successfully");
      } else {
        // Create new settings
        const createData = {
          coins_per_month: parseInt(verifiedBadgeCoins) || 0,
          discount_percentage: parseFloat(verifiedBadgeDiscount) || 0,
          description: "Verified badge subscription pricing",
          is_active: true,
        };

        const response = await VerificationBadgeService.createVerifiedBadgeSettings(createData);

        // Update local state with response data
        if (response.data && response.data.data) {
          const settingsData = response.data.data;
          setVerifiedBadgeSettings(settingsData);
          // Handle both discount and discount_percentage field names
          const discountValue = settingsData.discount_percentage || settingsData.discount || verifiedBadgeDiscount;
          setVerifiedBadgeDiscount(discountValue.toString());
          // Handle both coins and coins_per_month field names
          const coinsValue = settingsData.coins_per_month || settingsData.coins || verifiedBadgeCoins;
          setVerifiedBadgeCoins(coinsValue.toString());

          // Update cache
          if (!tabCache["pricing-reference"].data) {
            tabCache["pricing-reference"].data = {};
          }
          tabCache["pricing-reference"].data.verifiedBadgeSettings = settingsData;
        } else if (response.data) {
          setVerifiedBadgeSettings(response.data);
        }

        setIsEditingVerifiedBadge(false);
        Toast("success", response.data?.message || "Verified badge settings created successfully");
      }
    } catch (error) {
      console.error("Error saving verified badge settings:", error);
      Toast("error", error.response?.data?.message || "Failed to save verified badge settings");
    } finally {
      setIsLoadingVerifiedBadge(false);
    }
  };

  const handleVerifiedBadgeCancel = () => {
    // Reset to saved values from settings
    if (verifiedBadgeSettings) {
      // Handle both discount and discount_percentage field names
      const discountValue = verifiedBadgeSettings.discount_percentage || verifiedBadgeSettings.discount || "0";
      setVerifiedBadgeDiscount(discountValue.toString());
      // Handle both coins and coins_per_month field names
      const coinsValue = verifiedBadgeSettings.coins_per_month || verifiedBadgeSettings.coins || "0";
      setVerifiedBadgeCoins(coinsValue.toString());
    } else {
      setVerifiedBadgeDiscount("0");
      setVerifiedBadgeCoins("0");
    }
    setVerifiedBadgeDiscountError("");
    setIsEditingVerifiedBadge(false);
  };


  // Select dropdown styles (matching account verification page)
  const colourStyles = {
    control: (styles) => ({
      ...styles,
      minHeight: "40px",
      height: "40px",
      backgroundColor: "#0b1437",
      border: "none",
      boxShadow: "none",
      "&:hover": {
        border: "none",
      },
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      return {
        ...styles,
        backgroundColor: isDisabled
          ? undefined
          : isSelected
            ? "#24388a"
            : "#0b1437",
        color: isDisabled ? "#ccc" : "white",
        cursor: isDisabled ? "not-allowed" : "default",
        ":active": {
          ...styles[":active"],
          backgroundColor: !isDisabled
            ? isSelected
              ? "#24388a"
              : "#1a2d6b"
            : undefined,
        },
        ":hover": {
          backgroundColor: isSelected ? "#24388a" : "#1a2d6b",
          color: "white",
        },
      };
    },
    menu: (styles) => ({
      ...styles,
      backgroundColor: "#0b1437",
      borderRadius: "0.75rem",
      overflow: "hidden",
    }),
    menuList: (styles) => ({
      ...styles,
      backgroundColor: "#0b1437",
      padding: 0,
    }),
    singleValue: (styles) => ({
      ...styles,
      color: "white",
    }),
    input: (styles) => ({
      ...styles,
      color: "white",
    }),
  };

  return (
    <>
      <style>{`
        input[type="date"]::-webkit-calendar-picker-indicator,
        input[type="datetime-local"]::-webkit-calendar-picker-indicator {
          filter: brightness(0) invert(1);
          cursor: pointer;
          opacity: 1;
        }
        input[type="date"]::-moz-calendar-picker-indicator,
        input[type="datetime-local"]::-moz-calendar-picker-indicator {
          filter: brightness(0) invert(1);
          cursor: pointer;
          opacity: 1;
        }
        .modal-scrollable {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .modal-scrollable::-webkit-scrollbar {
          display: none;
        }
        /* Custom scrollbar for reorder modal - minimal, modern, no arrows */
        .reorder-modal-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(156, 163, 175, 0.15) transparent;
          -webkit-overflow-scrolling: touch;
        }
        .reorder-modal-scroll:hover {
          scrollbar-color: rgba(156, 163, 175, 0.3) transparent;
        }
        .reorder-modal-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .reorder-modal-scroll::-webkit-scrollbar-button {
          display: none !important;
          height: 0 !important;
          width: 0 !important;
        }
        .reorder-modal-scroll::-webkit-scrollbar-button:start:decrement,
        .reorder-modal-scroll::-webkit-scrollbar-button:end:increment,
        .reorder-modal-scroll::-webkit-scrollbar-button:vertical:start:decrement,
        .reorder-modal-scroll::-webkit-scrollbar-button:vertical:end:increment {
          display: none !important;
          height: 0 !important;
          width: 0 !important;
        }
        .reorder-modal-scroll::-webkit-scrollbar-corner {
          display: none !important;
          background: transparent !important;
        }
        .reorder-modal-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .reorder-modal-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(156, 163, 175, 0.15);
          border-radius: 3px;
          transition: background-color 0.2s ease;
        }
        .reorder-modal-scroll:hover::-webkit-scrollbar-thumb {
          background-color: rgba(156, 163, 175, 0.3);
        }
        .reorder-modal-scroll::-webkit-scrollbar-thumb:hover {
          background-color: rgba(156, 163, 175, 0.5);
        }
        .reorder-modal-scroll::-webkit-scrollbar-thumb:active {
          background-color: rgba(156, 163, 175, 0.6);
        }
        @media (max-width: 320px) {
          .add-promo-btn {
            font-size: 0.80rem !important;
            padding: 0.5rem 0.85rem !important;
          }
          .add-promo-btn-icon {
            width: 0.875rem !important;
            height: 0.875rem !important;
          }
          .add-coin-btn {
            font-size: 0.80rem !important;
            padding: 0.5rem 0.85rem !important;
          }
          .modal-btn {
            font-size: 0.80rem !important;
            padding: 0.5rem 0.85rem !important;
          }
        }
     @media (min-width: 376px) and (max-width: 530px) {
  .promotion-header-container {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .promotion-add-button-container {
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-between !important; /* 🔥 KEY FIX */
    align-items: center !important;
    width: 100% !important;
    margin-top: -5px !important;
  }

  .promotion-add-button-container .edit-order-btn,
  .promotion-add-button-container .add-coin-btn {
    margin: 0 !important;
    align-self: auto !important;
  }
}

        @media (max-width: 375px) {
          .promotion-header-container {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
          .promotion-dropdown-container {
            width: 100% !important;
            margin-top: 0 !important;
          }
          .promotion-dropdown-container .select__control {
            width: 185px !important;
          }
          .promotion-add-button-container {
            width: 100% !important;
            margin-top: -6px !important;
            justify-content: flex-start !important;
            flex-direction: column !important;
            gap: 0.5rem !important;
          }
          .promotion-add-button-container button {
            width: 185px !important;
          }
          .add-coin-btn {
            font-size: 0.80rem !important;
            padding: 0.5rem 0.85rem !important;
            align-self: flex-start !important;
          }
        }
      `}</style>
      <div className="mt-3 grid h-full grid-cols-1 gap-5">
        <div className="col-span-1 h-full w-full rounded-xl 2xl:col-span-1">
          <div ref={headerRef} className="flex justify-between gap-3 pb-4 promotion-header-container">
            <div className="mt-4 flex h-fit items-center promotion-dropdown-container">
              {/* Tab Dropdown Selector */}
              <Select
                className="h-full w-[185px] py-1 !text-white"
                classNamePrefix="select"
                value={[
                  { label: "Coin Packages", value: "coin-packages" },
                  { label: "Promo Code", value: "promo-code" },
                  { label: "Pricing Reference", value: "pricing-reference" }
                ].find(option => option.value === activeTab)}
                name="tab-selector"
                options={[
                  { label: "Coin Packages", value: "coin-packages" },
                  { label: "Promo Code", value: "promo-code" },
                  { label: "Pricing Reference", value: "pricing-reference" }
                ]}
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => option.label}
                isOptionSelected={(option) => option.value === activeTab}
                onChange={(option) => setActiveTab(option.value)}
                styles={colourStyles}
              />
            </div>
            <div className="mt-4 flex h-fit items-center gap-3 promotion-add-button-container">
              {activeTab === "promo-code" && (
                <>
                  <button
                    onClick={handlePromoCodeEditOrderClick}
                    className="add-promo-btn flex items-center gap-2 rounded-full bg-primary-gradient px-4 py-2.5 text-sm font-medium text-white/90 transition duration-200 hover:bg-gray-700"
                  >
                    Edit Order
                  </button>
                  <button
                    onClick={handleAddCodeName}
                    className="add-promo-btn flex items-center gap-2 rounded-full bg-primary-gradient px-4 py-3 text-sm font-medium text-white/90 transition duration-200 hover:opacity-90"
                  >
                    <FiPlus className="h-4 w-4" />
                    Add Promo Code
                  </button>
                </>
              )}
              {activeTab === "coin-packages" && (
                <>
                  <button
                    onClick={handleEditOrderClick}
                    className="add-coin-btn flex items-center gap-2 rounded-full bg-primary-gradient px-4 py-2.5 text-sm font-medium text-white/90 transition duration-200 hover:bg-gray-700"
                  >
                    Edit Order
                  </button>
                  <button
                    onClick={handleAddCoinPackage}
                    className="add-coin-btn flex items-center gap-2 rounded-full bg-primary-gradient px-4 py-2.5 text-sm font-medium text-white/90 transition duration-200 hover:opacity-90"
                  >
                    <FiPlus className="h-4 w-4" />
                    Add Coin Package
                  </button>
                </>
              )}
            </div>
          </div>
          <div ref={cardContainerRef} className="-mt-1 relative">
            {/* Promo Code Tab */}
            {activeTab === "promo-code" && (isLoadingList && tabCache["promo-code"]?.firstLoad && !tabCache["promo-code"]?.data ? (
              <div
                className="fixed"
                style={{
                  top: '50vh',
                  left: '57vw',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 40,
                  margin: 0,
                  padding: 0,
                  width: 'auto',
                  height: 'auto'
                }}
              >
                <img
                  src={Loader}
                  alt="Loading..."
                  className="h-16 w-16 block"
                  style={{ margin: '0 auto', padding: 0, display: 'block' }}
                />
              </div>
            ) : data.length === 0 ? (
              <div className="py-8 text-center text-white/90">
                No promo codes found
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 3xl:grid-cols-4 items-stretch">
                  {visiblePromoCards.map((item, index) => {
                    // Use first card as sample for height measurement
                    const isFirstCard = index === 0 && startIndex === 0;
                    const isExpired = isPromoExpired(item.endDate);
                    const isDeleted = item.deleted || deletedPromoCodeIds.has(item.id);
                    const isActive = isDeleted ? false : getEffectiveStatus(item);

                    return (
                      <SortablePromoCard
                        key={item.id}
                        item={item}
                        index={index}
                        isFirstCard={isFirstCard}
                        cardSampleRef={cardSampleRef}
                        isExpired={isExpired}
                        isDeleted={isDeleted}
                        isActive={isActive}
                        activeDropdown={activeDropdown}
                        activeMenuDropdown={activeMenuDropdown}
                        dropdownRef={dropdownRef}
                        menuDropdownRefs={menuDropdownRefs}
                        setActiveDropdown={setActiveDropdown}
                        setActiveMenuDropdown={setActiveMenuDropdown}
                        handleStatusChange={handleStatusChange}
                        handleEditClick={handleEditClick}
                        handleDeleteClick={handleDeleteClick}
                        deletedPromoCodeIds={deletedPromoCodeIds}
                      />
                    );
                  })}
                </div>

                {/* Google-style Pagination */}
                {!isLoadingList && visiblePromoCards.length > 0 && totalPages > 1 && (
                  <div className="mt-3 pt-3">
                    <GooglePagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                      pageSize={cardsPerPage}
                      onPageChange={handlePageChange}
                      className="w-full"
                    />
                  </div>
                )}
              </>
            ))}

            {/* Coin Packages Tab */}
            {activeTab === "coin-packages" && (isLoadingCoinPackages && tabCache["coin-packages"]?.firstLoad && !tabCache["coin-packages"]?.data ? (
              <div
                className="fixed"
                style={{
                  top: '50vh',
                  left: '57vw',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 40,
                  margin: 0,
                  padding: 0,
                  width: 'auto',
                  height: 'auto'
                }}
              >
                <img
                  src={Loader}
                  alt="Loading..."
                  className="h-16 w-16 block"
                  style={{ margin: '0 auto', padding: 0, display: 'block' }}
                />
              </div>
            ) : coinPackagesData.length === 0 ? (
              <div className="py-8 text-center text-white/90">
                No coin packages found
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 3xl:grid-cols-4 items-stretch">
                  {visibleCoinPackages.map((item, index) => {
                    const itemId = getCoinPackageItemId(item, index);
                    const isDeleted = item.deleted || deletedCoinPackageIds.has(item.id || item._id || item.package_id);
                    const isActive = isDeleted ? false : (item.status === "active");

                    return (
                      <CoinPackageCard
                        key={item.id}
                        item={item}
                        index={index}
                        itemId={itemId}
                        isDeleted={isDeleted}
                        isActive={isActive}
                        activeCoinPackageDropdown={activeCoinPackageDropdown}
                        openCoinPackageMenuId={openCoinPackageMenuId}
                        coinPackageStatusDropdownRefs={coinPackageStatusDropdownRefs}
                        coinPackageMenuDropdownRefs={coinPackageMenuDropdownRefs}
                        setActiveCoinPackageDropdown={setActiveCoinPackageDropdown}
                        setOpenCoinPackageMenuId={setOpenCoinPackageMenuId}
                        handleCoinPackageStatusChange={handleCoinPackageStatusChange}
                        handleCoinPackageEditClick={handleCoinPackageEditClick}
                        handleCoinPackageDeleteClick={handleCoinPackageDeleteClick}
                      />
                    );
                  })}
                </div>

                {!isLoadingCoinPackages && visibleCoinPackages.length > 0 && coinPackageTotalPages > 1 && (
                  <div className="mt-3 pt-3">
                    <GooglePagination
                      currentPage={coinPackageCurrentPage}
                      totalPages={coinPackageTotalPages}
                      totalCount={coinPackageTotalCount}
                      pageSize={coinPackageItemsPerPage}
                      onPageChange={handleCoinPackagePageChange}
                      className="w-full"
                    />
                  </div>
                )
                }
              </>
            ))}

            {/* Pricing Reference Tab */}
            {
              activeTab === "pricing-reference" && (
                <div className="m-auto h-full min-h-[calc(100vh-212px)]">
                  <div className="mt-1">
                    <div className="-mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 py-4 items-stretch">
                      {/* Card 1: Reference Rewards */}
                      <Card extra="w-full pt-6 pb-6 px-6 shadow-sm hover:shadow-md transition-shadow !bg-[#192242] !rounded-xl flex flex-col h-full">
  <div className="flex flex-col h-full">
    <div className="flex items-center justify-between">
      <h2 className="text-2xl font-bold text-white">
        Reference Rewards
      </h2>
    </div>

    <p className="text-lg text-white/90 leading-relaxed flex-1 my-4">
      Set and manage the reward amount users earn when they refer new members. Adjust the value to encourage user growth.
    </p>

    <div className="mt-auto flex-shrink-0">
      {isFetchingReference &&
      tabCache["pricing-reference"]?.firstLoad &&
      !tabCache["pricing-reference"]?.data ? (
        <div className="flex items-center justify-center py-8">
          <img src={Loader} alt="Loading..." className="w-16 h-16" />
        </div>
      ) : (
        <>
          {isEditingReference ? (
            /* ================= EDIT MODE ================= */
            <div className="flex flex-col space-y-4 -pt-1">
              <div className="flex gap-4 items-end">
                {/* Coins for Referrer */}
                <div className="flex flex-col space-y-2 w-32 sm:w-40">
                  <label className="text-sm sm:text-lg text-white/90 font-medium whitespace-nowrap">
                    Coins for Referrer
                  </label>
                  <input
                    type="number"
                    value={coinsForReferrer}
                    onChange={(e) => setCoinsForReferrer(e.target.value)}
                    className="rounded-md border border-gray-600 bg-navy-800 px-3 py-1 h-[34px] sm:h-[40px] text-white"
                  />
                </div>

                {/* Coins for Referee */}
                <div className="flex flex-col space-y-2 w-32 sm:w-40">
                  <label className="text-sm sm:text-lg text-white/90 font-medium whitespace-nowrap">
                    Coins for Referee
                  </label>
                  <input
                    type="number"
                    value={coinsForReferee}
                    onChange={(e) => setCoinsForReferee(e.target.value)}
                    className="rounded-md border border-gray-600 bg-navy-800 px-3 py-1 h-[34px] sm:h-[40px] text-white"
                  />
                </div>

                {/* Buttons */}
                <div className="flex items-center gap-2 mb-1 ml-auto">
                  <button
                    onClick={handleReferenceSave}
                    disabled={isLoadingReference}
                    className="w-24 h-[34px] sm:h-[40px] rounded-md bg-primary-gradient text-white font-bold"
                  >
                    {isLoadingReference ? "Saving..." : "Save"}
                  </button>
                  <button
                    onClick={handleReferenceCancel}
                    disabled={isLoadingReference}
                    className="w-24 h-[34px] sm:h-[40px] rounded-md bg-gray-600 text-white"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* ================= DISPLAY MODE ================= */
            <div className="flex flex-col space-y-4 -pt-1">
              <div className="flex gap-4 items-end">
                {/* Coins for Referrer (DOUBLE CLICK ENABLED) */}
                <div
                  className="flex flex-col space-y-2 w-32 sm:w-40 cursor-pointer"
                  onDoubleClick={handleReferenceDoubleClick}
                >
                  <label className="text-sm sm:text-lg text-white/90 font-medium">
                    Coins for Referrer
                  </label>
                  <div className="h-[34px] sm:h-[40px] rounded-md border border-gray-600 bg-navy-800 px-3 py-1 flex items-center">
                    <span className="text-white font-medium">
                      {coinsForReferrer}
                    </span>
                  </div>
                </div>

                {/* Coins for Referee (DOUBLE CLICK ENABLED) */}
                <div
                  className="flex flex-col space-y-2 w-32 sm:w-40 cursor-pointer"
                  onDoubleClick={handleReferenceDoubleClick}
                >
                  <label className="text-sm sm:text-lg text-white/90 font-medium">
                    Coins for Referee
                  </label>
                  <div className="h-[34px] sm:h-[40px] rounded-md border border-gray-600 bg-navy-800 px-3 py-1 flex items-center">
                    <span className="text-white font-medium">
                      {coinsForReferee}
                    </span>
                  </div>
                </div>

                {/* Edit Button (UNCHANGED) */}
                <div className="mb-1 ml-auto">
                  <button
                    onClick={handleReferenceEditClick}
                    className="w-24 h-[34px] sm:h-[40px] rounded-md bg-primary-gradient text-white font-bold"
                  >
                    Edit
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  </div>
</Card>


                      {/* Card 2: Theme Plan */}
                      <Card extra="w-full pt-6 pb-6 px-6 shadow-sm hover:shadow-md transition-shadow !bg-[#192242] !rounded-xl flex flex-col h-full">
  <div className="flex flex-col h-full">
    <h2 className="text-2xl font-bold text-white flex-shrink-0">
      Theme Plan
    </h2>

    <p className="text-lg text-white/90 leading-relaxed flex-1 my-4">
      Set and manage the discount percentage and coins per month for theme plans. Adjust these values to configure theme plan pricing.
    </p>

    <div className="mt-auto flex-shrink-0">
      {isFetchingThemePlan &&
      tabCache["pricing-reference"]?.firstLoad &&
      !tabCache["pricing-reference"]?.data ? (
        <div className="flex items-center justify-center py-8">
          <img src={Loader} alt="Loading..." className="w-16 h-16" />
        </div>
      ) : !themePlanSettings ? (
        <>
          {isAddingThemePlan ? (
            /* ================= ADD MODE ================= */
            <div className="flex flex-col space-y-4 -pt-1">
              <div className="flex items-start gap-3 sm:gap-4">
                {/* Discount */}
                <div className="flex flex-col space-y-2 w-32 sm:w-40">
                  <label className="text-sm sm:text-lg text-white/90 font-medium">
                    Discount
                  </label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-white/70">
                      %
                    </span>
                    <input
                      type="number"
                      value={themePlanDiscount}
                      onChange={(e) => setThemePlanDiscount(e.target.value)}
                      className="w-full rounded-md border border-gray-600 bg-navy-800 pl-6 pr-3 py-1 h-[34px] sm:h-[40px] text-white"
                    />
                  </div>
                </div>

                {/* Coins */}
                <div className="flex flex-col space-y-2 w-32 sm:w-40">
                  <label className="text-sm sm:text-lg text-white/90 font-medium">
                    Coins
                  </label>
                  <input
                    type="number"
                    value={themePlanCoinsPerMonth}
                    onChange={(e) => setThemePlanCoinsPerMonth(e.target.value)}
                    className="w-full rounded-md border border-gray-600 bg-navy-800 px-3 py-1 h-[34px] sm:h-[40px] text-white"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={handleThemePlanAdd}
                  className="w-24 rounded-md bg-primary-gradient py-2 text-white font-bold"
                >
                  Add
                </button>
                <button
                  onClick={handleThemePlanAddCancel}
                  className="w-24 rounded-md bg-gray-600 py-2 text-white"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center py-8">
              <button
                onClick={handleThemePlanAddClick}
                className="rounded-md bg-primary-gradient px-6 py-3 text-white font-bold"
              >
                Add Theme Plan
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          {isEditingThemePlan ? (
            /* ================= EDIT MODE ================= */
            <div className="flex flex-col space-y-4 -pt-1">
              <div className="flex items-end gap-3 sm:gap-4">
                {/* Discount */}
                <div className="flex flex-col space-y-2 w-32 sm:w-40">
                  <label className="text-sm sm:text-lg text-white/90 font-medium">
                    Discount
                  </label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-white/70">
                      %
                    </span>
                    <input
                      type="number"
                      value={themePlanDiscount}
                      onChange={(e) => setThemePlanDiscount(e.target.value)}
                      className="w-full rounded-md border border-gray-600 bg-navy-800 pl-6 pr-3 py-1 h-[34px] sm:h-[40px] text-white"
                    />
                  </div>
                </div>

                {/* Coins */}
                <div className="flex flex-col space-y-2 w-32 sm:w-40">
                  <label className="text-sm sm:text-lg text-white/90 font-medium">
                    Coins
                  </label>
                  <input
                    type="number"
                    value={themePlanCoinsPerMonth}
                    onChange={(e) => setThemePlanCoinsPerMonth(e.target.value)}
                    className="w-full rounded-md border border-gray-600 bg-navy-800 px-3 py-1 h-[34px] sm:h-[40px] text-white"
                  />
                </div>

                {/* Buttons */}
                <div className="flex gap-2 ml-auto">
                  <button
                    onClick={handleThemePlanSave}
                    className="w-24 h-[34px] sm:h-[40px] rounded-md bg-primary-gradient text-white font-bold"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleThemePlanCancel}
                    className="w-24 h-[34px] sm:h-[40px] rounded-md bg-gray-600 text-white"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* ================= DISPLAY MODE ================= */
            <div className="flex flex-col space-y-4 -pt-1">
              <div className="flex items-end gap-3 sm:gap-4">
                {/* Discount (DOUBLE CLICK ENABLED) */}
                <div
                  className="flex flex-col space-y-2 w-32 sm:w-40 cursor-pointer"
                  onDoubleClick={handleThemePlanDoubleClick}
                >
                  <label className="text-sm sm:text-lg text-white/90 font-medium">
                    Discount
                  </label>
                  <div className="relative h-[34px] sm:h-[40px] rounded-md border border-gray-600 bg-navy-800 pl-6 pr-3 py-1 flex items-center">
                    <span className="absolute left-2 text-white/70">%</span>
                    <span className="text-white font-medium">
                      {themePlanDiscount}
                    </span>
                  </div>
                </div>

                {/* Coins (DOUBLE CLICK ENABLED) */}
                <div
                  className="flex flex-col space-y-2 w-32 sm:w-40 cursor-pointer"
                  onDoubleClick={handleThemePlanDoubleClick}
                >
                  <label className="text-sm sm:text-lg text-white/90 font-medium">
                    Coins
                  </label>
                  <div className="h-[34px] sm:h-[40px] rounded-md border border-gray-600 bg-navy-800 px-3 py-1 flex items-center">
                    <span className="text-white font-medium">
                      {themePlanCoinsPerMonth}
                    </span>
                  </div>
                </div>

                {/* Edit Button (UNCHANGED) */}
                <div className="ml-auto">
                  <button
                    onClick={handleThemePlanEditClick}
                    className="w-24 rounded-md bg-primary-gradient py-2 text-white font-bold"
                  >
                    Edit
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  </div>
</Card>


                      {/* Card 3: Emoji Plan */}
                      <Card extra="w-full pt-6 pb-6 px-6 shadow-sm hover:shadow-md transition-shadow !bg-[#192242] !rounded-xl flex flex-col h-full">
  <div className="flex flex-col h-full">
    <h2 className="text-2xl font-bold text-white flex-shrink-0">
      Emoji Plan
    </h2>

    <p className="text-lg text-white/90 leading-relaxed flex-1 my-4">
      Set and manage the discount percentage and coin value for emoji plans. Adjust these values to configure emoji plan pricing.
    </p>

    <style>{`
      @media (min-width: 1000px) and (max-width: 1150px) {
        .emoji-plan-edit-spacing, .emoji-plan-display-spacing {
          padding-top: 1rem !important;
        }
      }
      @media (min-width: 1000px) and (max-width: 1440px) {
        .emoji-discount-shift {
          margin-top: 1.7rem;
        }
      }
    `}</style>

    <div className="mt-auto flex-shrink-0">
      {isFetchingEmojiPlan &&
      tabCache["pricing-reference"]?.firstLoad &&
      !tabCache["pricing-reference"]?.data ? (
        <div className="flex items-center justify-center py-8">
          <img src={Loader} alt="Loading..." className="w-16 h-16" />
        </div>
      ) : (
        <>
          {isEditingEmojiPlan ? (
            /* ================= EDIT MODE ================= */
            <div className="flex flex-col space-y-4 -pt-1 emoji-plan-edit-spacing">
              <div className="flex items-end gap-3 sm:gap-4">
                {/* Discount */}
                <div className="flex flex-col space-y-2 w-32 sm:w-40 emoji-discount-shift">
                  <label className="text-sm sm:text-lg text-white/90 font-medium">
                    Discount
                  </label>
                  <div className="relative">
                    <span className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 text-sm sm:text-base font-medium text-white/70">
                      %
                    </span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={emojiPlanDiscount}
                      onChange={(e) => setEmojiPlanDiscount(e.target.value)}
                      className="w-full rounded-md border border-gray-600 bg-navy-800 pl-6 sm:pl-7 pr-3 py-1 h-[34px] sm:h-[40px] text-white"
                    />
                  </div>
                </div>

                {/* Coins */}
                <div className="flex flex-col space-y-2 w-32 sm:w-40">
                  <label className="text-sm sm:text-lg text-white/90 font-medium">
                    Coins / Per Month
                  </label>
                  <input
                    type="number"
                    value={emojiPlanCoins}
                    onChange={(e) => setEmojiPlanCoins(e.target.value)}
                    className="w-full rounded-md border border-gray-600 bg-navy-800 px-3 py-1 h-[34px] sm:h-[40px] text-white"
                  />
                </div>

                {/* Buttons */}
                <div className="flex items-center gap-2 mb-1 ml-auto emoji-discount-shift">
                  <button
                    onClick={handleEmojiPlanSave}
                    className="w-24 h-[34px] sm:h-[40px] rounded-md bg-primary-gradient text-white font-bold"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleEmojiPlanCancel}
                    className="w-24 h-[34px] sm:h-[40px] rounded-md bg-gray-600 text-white"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* ================= DISPLAY MODE ================= */
            <div className="flex flex-col space-y-4 -pt-1 emoji-plan-display-spacing">
              <div className="flex items-end gap-3 sm:gap-4">
                {/* Discount (DOUBLE CLICK ENABLED) */}
                <div
                  className="flex flex-col space-y-2 w-32 sm:w-40 emoji-discount-shift"
                  onDoubleClick={handleEmojiDoubleClick}
                >
                  <label className="text-sm sm:text-lg text-white/90 font-medium">
                    Discount
                  </label>
                  <div className="relative w-full h-[34px] sm:h-[40px] rounded-md border border-gray-600 bg-navy-800 pl-6 pr-3 py-1 flex items-center cursor-pointer">
                    <span className="absolute left-2 text-white/70">%</span>
                    <span className="text-white font-medium">
                      {emojiPlanDiscount || "0.00"}
                    </span>
                  </div>
                </div>

                {/* Coins (DOUBLE CLICK ENABLED) */}
                <div
                  className="flex flex-col space-y-2 w-32 sm:w-40"
                  onDoubleClick={handleEmojiDoubleClick}
                >
                  <label className="text-sm sm:text-lg text-white/90 font-medium">
                    Coins / Per Month
                  </label>
                  <div className="w-full h-[34px] sm:h-[40px] rounded-md border border-gray-600 bg-navy-800 px-3 py-1 flex items-center cursor-pointer">
                    <span className="text-white font-medium">
                      {emojiPlanCoins || "0"}
                    </span>
                  </div>
                </div>

                {/* Edit Button (UNCHANGED) */}
                <div className="mb-1 ml-auto emoji-discount-shift">
                  <button
                    onClick={handleEmojiPlanEditClick}
                    className="w-24 h-[34px] sm:h-[40px] rounded-md bg-primary-gradient text-white font-bold"
                  >
                    Edit
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  </div>
</Card>


                      {/* Card 4: Verified Badge Plan */}
                      <Card extra="w-full pt-6 pb-6 px-6 shadow-sm hover:shadow-md transition-shadow !bg-[#192242] !rounded-xl flex flex-col h-full">
  <div className="flex flex-col h-full">
    <div className="flex items-center justify-between">
      <h2 className="text-2xl font-bold text-white flex-shrink-0">
        Verified Badge Plan
      </h2>
    </div>

    <p className="text-lg text-white/90 leading-relaxed flex-1 my-4">
      Set and manage verified badge pricing, duration, and benefits for users. Adjust values to configure verified badge access.
    </p>

    <style>{`
      @media (min-width: 1000px) and (max-width: 1100px) {
        .emoji-plan-edit-spacing, .emoji-plan-display-spacing {
          padding-top: 1rem !important;
        }
      }
      @media (min-width: 1000px) and (max-width: 1440px) {
        .emoji-discount-shift {
          margin-top: 1.7rem;
        }
      }
      @media (min-width: 1140px) and (max-width: 1200px) {
        .emoji-discount-shift {
          margin-top: 1px;
        }
      }
    `}</style>

    <div className="mt-auto flex-shrink-0">
      {isFetchingVerifiedBadge &&
      tabCache["pricing-reference"]?.firstLoad &&
      !tabCache["pricing-reference"]?.data ? (
        <div className="flex items-center justify-center py-8">
          <img src={Loader} alt="Loading..." className="w-16 h-16" />
        </div>
      ) : (
        <>
          {isEditingVerifiedBadge ? (
            /* ================= EDIT MODE ================= */
            <div className="flex flex-col space-y-4 -pt-1 emoji-plan-edit-spacing">
              <div className="flex items-end gap-3 sm:gap-4">
                {/* Discount */}
                <div className="flex flex-col space-y-2 w-32 sm:w-40 emoji-discount-shift">
                  <label className="text-sm sm:text-lg text-white/90 font-medium">
                    Discount
                  </label>
                  <div className="relative">
                    <span className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 text-sm sm:text-base font-medium text-white/70">
                      %
                    </span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={verifiedBadgeDiscount}
                      onChange={(e) => {
                        setVerifiedBadgeDiscount(e.target.value);
                        if (verifiedBadgeDiscountError) {
                          setVerifiedBadgeDiscountError("");
                        }
                      }}
                      className={`w-full rounded-md border ${verifiedBadgeDiscountError ? "border-red-500" : "border-gray-600"} bg-navy-800 pl-6 sm:pl-7 pr-3 py-1 h-[34px] sm:h-[40px] text-white`}
                    />
                  </div>
                  {verifiedBadgeDiscountError && (
                    <p className="text-xs text-red-400 mt-1">
                      {verifiedBadgeDiscountError}
                    </p>
                  )}
                </div>

                {/* Coins */}
                <div className="flex flex-col space-y-2 w-32 sm:w-40">
                  <label className="text-sm sm:text-lg text-white/90 font-medium">
                    Coins / Per Month
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={verifiedBadgeCoins}
                    onChange={(e) => setVerifiedBadgeCoins(e.target.value)}
                    className="w-full rounded-md border border-gray-600 bg-navy-800 px-3 py-1 h-[34px] sm:h-[40px] text-white"
                  />
                </div>

                {/* Buttons */}
                <div className="flex items-center gap-2 mb-1 ml-auto emoji-discount-shift">
                  <button
                    onClick={handleVerifiedBadgeSave}
                    disabled={isLoadingVerifiedBadge}
                    className="w-24 h-[34px] sm:h-[40px] rounded-md bg-primary-gradient text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoadingVerifiedBadge ? "Saving..." : "Save"}
                  </button>
                  <button
                    onClick={handleVerifiedBadgeCancel}
                    disabled={isLoadingVerifiedBadge}
                    className="w-24 h-[34px] sm:h-[40px] rounded-md bg-gray-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* ================= DISPLAY MODE ================= */
            <div className="flex flex-col space-y-4 -pt-1 emoji-plan-display-spacing">
              <div className="flex items-end gap-3 sm:gap-4">
                {/* Discount (DOUBLE CLICK ENABLED) */}
                <div
                  className="flex flex-col space-y-2 w-32 sm:w-40 emoji-discount-shift cursor-pointer"
                  onDoubleClick={handleVerifiedBadgeDoubleClick}
                >
                  <label className="text-sm sm:text-lg text-white/90 font-medium">
                    Discount
                  </label>
                  <div className="relative h-[34px] sm:h-[40px] rounded-md border border-gray-600 bg-navy-800 pl-6 pr-3 py-1 flex items-center">
                    <span className="absolute left-2 text-white/70">%</span>
                    <span className="text-white font-medium">
                      {verifiedBadgeDiscount || "0"}
                    </span>
                  </div>
                </div>

                {/* Coins (DOUBLE CLICK ENABLED) */}
                <div
                  className="flex flex-col space-y-2 w-32 sm:w-40 cursor-pointer"
                  onDoubleClick={handleVerifiedBadgeDoubleClick}
                >
                  <label className="text-sm sm:text-lg text-white/90 font-medium">
                    Coins / Per Month
                  </label>
                  <div className="h-[34px] sm:h-[40px] rounded-md border border-gray-600 bg-navy-800 px-3 py-1 flex items-center">
                    <span className="text-white font-medium">
                      {verifiedBadgeCoins || "0"}
                    </span>
                  </div>
                </div>

                {/* Edit Button (UNCHANGED) */}
                <div className="mb-1 ml-auto emoji-discount-shift">
                  <button
                    onClick={handleVerifiedBadgeEditClick}
                    className="w-24 h-[34px] sm:h-[40px] rounded-md bg-primary-gradient text-white font-bold"
                  >
                    Edit
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  </div>
</Card>

                    </div>
                  </div>
                </div>
              )
            }

          </div >
        </div >
      </div >

      {/* Edit Modal */}
      {
        isEditModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fade-in px-4"
            onClick={handleCloseModal}
          >
            <div
              className="w-full max-w-md rounded-xl bg-navy-800 p-6 shadow-lg animate-fade-in modal-scrollable"
              style={{ maxHeight: '90vh', overflowY: 'auto' }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="mb-3 text-xl font-bold text-white/90">Edit Promo Code</h2>

              <div className="space-y-4">
                {/* Promo Code */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-white/90">
                    Promo Code
                  </label>
                  <input
                    type="text"
                    value={editFormData.code}
                    onChange={(e) => {
                      setEditFormData({
                        ...editFormData,
                        code: e.target.value,
                      });
                      if (editFormErrors.code) {
                        setEditFormErrors({
                          ...editFormErrors,
                          code: "",
                        });
                      }
                    }}
                    className={`w-full rounded-md border ${editFormErrors.code
                      ? "border-red-500"
                      : "border-gray-600 hover:border-blue-400"
                      } bg-navy-700 px-3 py-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400`}
                    placeholder="e.g., SUMMER2024"
                  />
                  {editFormErrors.code && (
                    <p className="mt-1 text-sm text-red-400">
                      {editFormErrors.code}
                    </p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-white/90">
                    Description
                  </label>
                  <textarea
                    value={editFormData.description}
                    onChange={(e) => {
                      setEditFormData({
                        ...editFormData,
                        description: e.target.value,
                      });
                      if (editFormErrors.description) {
                        setEditFormErrors({
                          ...editFormErrors,
                          description: "",
                        });
                      }
                    }}
                    rows={2}
                    className={`w-full rounded-md border ${editFormErrors.description
                      ? "border-red-500"
                      : "border-gray-600 hover:border-blue-400"
                      } bg-navy-700 px-3 pt-2 pb-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 resize-none`}
                    placeholder="Add offer info"
                  />
                  {editFormErrors.description && (
                    <p className="mt-1 text-sm text-red-400">
                      {editFormErrors.description}
                    </p>
                  )}
                </div>

                {/* Discount Value */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label className="text-sm font-medium text-white/90">
                      Discount Value
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">Use Percentage</span>
                      <Switch
                        checked={editFormData.discountType === "%"}
                        onChange={(e) => {
                          const newType = e.target.checked ? "%" : "$";
                          setEditFormData({
                            ...editFormData,
                            discountType: newType,
                            // Clear discount value when switching types
                            discountValue: "",
                          });
                          if (editFormErrors.discountValue) {
                            setEditFormErrors({
                              ...editFormErrors,
                              discountValue: "",
                            });
                          }
                        }}
                        color="purple"
                      />
                    </div>
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      value={editFormData.discountValue}
                      onChange={(e) => {
                        // Only allow numbers and decimal point
                        const value = e.target.value.replace(/[^0-9.]/g, "");
                        setEditFormData({
                          ...editFormData,
                          discountValue: value,
                        });
                        if (editFormErrors.discountValue) {
                          setEditFormErrors({
                            ...editFormErrors,
                            discountValue: "",
                          });
                        }
                      }}
                      className={`w-full rounded-md border ${editFormErrors.discountValue
                        ? "border-red-500"
                        : "border-gray-600 hover:border-blue-400"
                        } bg-navy-700 px-3 py-1.5 pr-8 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all duration-200`}
                      placeholder="Add discount"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 pointer-events-none">
                      <span className="text-white/70 text-sm font-medium">
                        {editFormData.discountType === "%" ? "%" : "$"}
                      </span>
                      <div className="tooltip tooltip-top" data-tip={editFormData.discountType === "%" ? "Discount will be applied as percentage." : "Discount will be applied\nas a fixed amount."}>
                        <Info size={16} className="text-blue-400 cursor-help pointer-events-auto" />
                      </div>
                    </div>
                  </div>
                  {editFormErrors.discountValue && (
                    <p className="mt-1 text-sm text-red-400">
                      {editFormErrors.discountValue}
                    </p>
                  )}
                </div>

                {/* Usage Limit */}
                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm font-medium text-white/90">
                    Max Redemptions
                    <div className="tooltip tooltip-top" data-tip="Maximum number of times this discount code can be redeemed.">
                      <Info size={16} className="text-blue-400 cursor-help" />
                    </div>
                  </label>
                  <input
                    type="number"
                    value={editFormData.maxUsers}
                    onChange={(e) => {
                      setEditFormData({
                        ...editFormData,
                        maxUsers: e.target.value,
                      });
                      if (editFormErrors.maxUsers) {
                        setEditFormErrors({
                          ...editFormErrors,
                          maxUsers: "",
                        });
                      }
                    }}
                    className={`w-full rounded-md border ${editFormErrors.maxUsers
                      ? "border-red-500"
                      : "border-gray-600 hover:border-blue-400"
                      } bg-navy-700 px-3 py-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400`}
                    placeholder="Add limit"
                    min="1"
                  />
                  {editFormErrors.maxUsers && (
                    <p className="mt-1 text-sm text-red-400">
                      {editFormErrors.maxUsers}
                    </p>
                  )}
                </div>

                {/* End Date Toggle */}
                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <label className="text-sm font-medium text-white/90">
                      End Date
                    </label>
                    <Switch
                      checked={editFormData.showEndDate}
                      onChange={(e) => {
                        setEditFormData({
                          ...editFormData,
                          showEndDate: e.target.checked,
                        });
                      }}
                      color="purple"
                    />
                  </div>

                  {/* End Date Field - Conditionally Rendered */}
                  {editFormData.showEndDate && (() => {
                    // Get start date from editingRow and format it for min attribute
                    let minDateValue = "";
                    if (editingRow && editingRow.startDate) {
                      if (editingRow.startDate.includes("T")) {
                        minDateValue = editingRow.startDate.substring(0, 16);
                      } else {
                        minDateValue = `${editingRow.startDate}T00:00`;
                      }
                    }

                    return (
                      <div className="transition-all duration-300 ease-in-out animate-fade-in">
                        <label className="mb-2 block text-sm font-medium text-white/90">
                          End Date
                        </label>
                        <DateTimeRangePicker
                          value={editFormData.endDate}
                          minDate={minDateValue}
                          openAbove={false}
                          onChange={(e) => {
                            const selectedEndDate = e.target.value;
                            const errors = { ...editFormErrors };

                            // Validate if end date is before start date
                            if (minDateValue && selectedEndDate) {
                              if (new Date(selectedEndDate) < new Date(minDateValue)) {
                                errors.endDate = "End date cannot be before start date";
                              } else {
                                // Clear error if valid
                                delete errors.endDate;
                              }
                            } else {
                              // Clear error if no date selected yet
                              delete errors.endDate;
                            }

                            setEditFormData({
                              ...editFormData,
                              endDate: selectedEndDate,
                            });
                            setEditFormErrors(errors);
                          }}
                          error={editFormErrors.endDate}
                          placeholder="Select end date and time"
                          showTime={true}
                        />
                        {editFormErrors.endDate && (
                          <p className="mt-1 text-sm text-red-400">
                            {editFormErrors.endDate}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Visibility Checkbox */}
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-white/90 flex items-center gap-2">
                    Promo code visibility on the app
                    <div className="relative group">
                      <IoMdInformationCircleOutline
                        className="h-4 w-4 cursor-help text-gray-400 transition-colors hover:text-gray-300"
                      />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 whitespace-nowrap pointer-events-none">
                        <div className="bg-navy-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
                          {editFormData.visibleOnApp ? "Visible to all users" : "This is a private promo code and is not visible to all users"}
                        </div>
                      </div>
                    </div>
                  </label>
                  <input
                    type="checkbox"
                    checked={editFormData.visibleOnApp}
                    onChange={(e) => {
                      setEditFormData({
                        ...editFormData,
                        visibleOnApp: e.target.checked,
                      });
                    }}
                    className="h-5 w-5 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-navy-800 cursor-pointer"
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="mt-6 flex justify-end gap-4">
                <button
                  onClick={handleCloseModal}
                  className="modal-btn rounded-md bg-gray-600 px-6 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveChanges}
                  disabled={isLoading}
                  className="modal-btn rounded-md bg-primary-gradient px-6 py-2 text-sm font-medium text-white/90 transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Add Promo Code Modal */}
      {
        isAddModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fade-in px-4"
            onClick={handleCloseAddModal}
          >
            <div
              className="w-full max-w-md rounded-xl bg-navy-800 p-6 shadow-lg animate-fade-in relative modal-scrollable"
              style={{ maxHeight: '90vh', overflowY: 'auto' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={handleCloseAddModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-white/90 transition-colors"
                title="Close"
              >
                <MdClose className="h-6 w-6" />
              </button>

              <h2 className="mb-4 text-xl font-bold text-white/90 pr-8">Add Promo Code</h2>

              <div className="space-y-4">
                {/* Promo Code */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-white/90">
                    Promo Code
                  </label>
                  <input
                    type="text"
                    value={addFormData.code}
                    onChange={(e) => {
                      setAddFormData({
                        ...addFormData,
                        code: e.target.value,
                      });
                      if (addFormErrors.code) {
                        setAddFormErrors({
                          ...addFormErrors,
                          code: "",
                        });
                      }
                    }}
                    className={`w-full rounded-md border ${addFormErrors.code
                      ? "border-red-500"
                      : "border-gray-600 hover:border-blue-400"
                      } bg-navy-700 px-3 py-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400`}
                    placeholder="e.g., SUMMER2024"
                  />
                  {addFormErrors.code && (
                    <p className="mt-1 text-sm text-red-400">
                      {addFormErrors.code}
                    </p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-white/90">
                    Description
                  </label>
                  <textarea
                    value={addFormData.description}
                    onChange={(e) => {
                      setAddFormData({
                        ...addFormData,
                        description: e.target.value,
                      });
                      if (addFormErrors.description) {
                        setAddFormErrors({
                          ...addFormErrors,
                          description: "",
                        });
                      }
                    }}
                    rows={1}
                    className={`w-full rounded-md border ${addFormErrors.description
                      ? "border-red-500"
                      : "border-gray-600 hover:border-blue-400"
                      } bg-navy-700 px-3 pt-2 pb-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 resize-none`}
                    placeholder="Add offer info"
                  />
                  {addFormErrors.description && (
                    <p className="mt-1 text-sm text-red-400">
                      {addFormErrors.description}
                    </p>
                  )}
                </div>

                {/* Discount Value */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label className="text-sm font-medium text-white/90">
                      Discount Value
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">Use Percentage</span>
                      <Switch
                        checked={addFormData.discountType === "%"}
                        onChange={(e) => {
                          const newType = e.target.checked ? "%" : "$";
                          setAddFormData({
                            ...addFormData,
                            discountType: newType,
                            // Clear discount value when switching types
                            discountValue: "",
                          });
                          if (addFormErrors.discountValue) {
                            setAddFormErrors({
                              ...addFormErrors,
                              discountValue: "",
                            });
                          }
                        }}
                        color="purple"
                      />
                    </div>
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      value={addFormData.discountValue}
                      onChange={(e) => {
                        // Only allow numbers and decimal point
                        const value = e.target.value.replace(/[^0-9.]/g, "");
                        setAddFormData({
                          ...addFormData,
                          discountValue: value,
                        });
                        if (addFormErrors.discountValue) {
                          setAddFormErrors({
                            ...addFormErrors,
                            discountValue: "",
                          });
                        }
                      }}
                      className={`w-full rounded-md border ${addFormErrors.discountValue
                        ? "border-red-500"
                        : "border-gray-600 hover:border-blue-400"
                        } bg-navy-700 px-3 py-1.5 pr-8 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all duration-200`}
                      placeholder="Add discount"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 pointer-events-none">
                      <span className="text-white/70 text-sm font-medium">
                        {addFormData.discountType === "%" ? "%" : "$"}
                      </span>
                      <div className="tooltip tooltip-top" data-tip={addFormData.discountType === "%" ? "Discount will be applied\nas percentage." : "Discount will be applied\nas a fixed amount."}>
                        <Info size={16} className="text-blue-400 cursor-help pointer-events-auto" />
                      </div>
                    </div>
                  </div>
                  {addFormErrors.discountValue && (
                    <p className="mt-1 text-sm text-red-400">
                      {addFormErrors.discountValue}
                    </p>
                  )}
                </div>

                {/* Usage Limit */}
                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm font-medium text-white/90">
                    Max Redemptions
                    <div className="tooltip tooltip-top" data-tip="Maximum number of times this\ndiscount code can be redeemed.">
                      <Info size={16} className="text-blue-400 cursor-help" />
                    </div>
                  </label>
                  <input
                    type="number"
                    value={addFormData.maxUsers}
                    onChange={(e) => {
                      setAddFormData({
                        ...addFormData,
                        maxUsers: e.target.value,
                      });
                      if (addFormErrors.maxUsers) {
                        setAddFormErrors({
                          ...addFormErrors,
                          maxUsers: "",
                        });
                      }
                    }}
                    className={`w-full rounded-md border ${addFormErrors.maxUsers
                      ? "border-red-500"
                      : "border-gray-600 hover:border-blue-400"
                      } bg-navy-700 px-3 py-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400`}
                    placeholder="Add limit"
                    min="1"
                  />
                  {addFormErrors.maxUsers && (
                    <p className="mt-1 text-sm text-red-400">
                      {addFormErrors.maxUsers}
                    </p>
                  )}
                </div>

                {/* Expiry Toggle */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label className="text-sm font-medium text-white/90">
                      Has Expiry?
                    </label>
                    <Switch
                      checked={addFormData.hasExpiry}
                      onChange={(e) => {
                        const hasExpiry = e.target.checked;
                        setAddFormData({
                          ...addFormData,
                          hasExpiry: hasExpiry,
                          // Clear endDate when toggle is turned off
                          endDate: hasExpiry ? addFormData.endDate : "",
                        });
                        // Clear endDate error when toggle is turned off
                        if (!hasExpiry && addFormErrors.endDate) {
                          const errors = { ...addFormErrors };
                          delete errors.endDate;
                          setAddFormErrors(errors);
                        }
                      }}
                      color="purple"
                    />
                  </div>

                  {/* End Date - Conditionally Rendered */}
                  {addFormData.hasExpiry && (
                    <div className="transition-all duration-300 ease-in-out animate-fade-in">
                      <label className="mb-2 block text-sm font-medium text-white/90">
                        End Date
                      </label>
                      <DateTimeRangePicker
                        value={addFormData.endDate}
                        minDate={(() => {
                          // Set minimum date to current date/time
                          const now = new Date();
                          const year = now.getFullYear();
                          const month = String(now.getMonth() + 1).padStart(2, '0');
                          const day = String(now.getDate()).padStart(2, '0');
                          const hours = String(now.getHours()).padStart(2, '0');
                          const minutes = String(now.getMinutes()).padStart(2, '0');
                          return `${year}-${month}-${day}T${hours}:${minutes}`;
                        })()}
                        openAbove={false}
                        onChange={(e) => {
                          const newEndDate = e.target.value;
                          setAddFormData({
                            ...addFormData,
                            endDate: newEndDate,
                          });
                          if (addFormErrors.endDate) {
                            setAddFormErrors({
                              ...addFormErrors,
                              endDate: "",
                            });
                          }
                          // Validate that end date is not in the past
                          if (newEndDate) {
                            const endDate = new Date(newEndDate);
                            const now = new Date();
                            if (endDate < now) {
                              setAddFormErrors({
                                ...addFormErrors,
                                endDate: "End date must be in the future",
                              });
                            } else {
                              // Clear error if valid
                              const errors = { ...addFormErrors };
                              delete errors.endDate;
                              setAddFormErrors(errors);
                            }
                          }
                        }}
                        error={addFormErrors.endDate}
                        placeholder="Select end date and time"
                        showTime={true}
                      />
                      {addFormErrors.endDate && (
                        <p className="mt-1 text-sm text-red-400">
                          {addFormErrors.endDate}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Visibility Checkbox */}
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-white/90 flex items-center gap-2">
                    Promo code visibility on the app
                    <div className="relative group">
                      <IoMdInformationCircleOutline
                        className="h-4 w-4 cursor-help text-gray-400 transition-colors hover:text-gray-300"
                      />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 whitespace-nowrap pointer-events-none">
                        <div className="bg-navy-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
                          {addFormData.visibleOnApp ? "Visible to all users" : "This is a private promo code and is not visible to all users"}
                        </div>
                      </div>
                    </div>
                  </label>
                  <input
                    type="checkbox"
                    checked={addFormData.visibleOnApp}
                    onChange={(e) => {
                      setAddFormData({
                        ...addFormData,
                        visibleOnApp: e.target.checked,
                      });
                    }}
                    className="h-5 w-5 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-navy-800 cursor-pointer"
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="mt-6 flex justify-end gap-4">
                <button
                  onClick={handleCloseAddModal}
                  className="modal-btn rounded-md bg-gray-600 px-6 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddPromoCode}
                  disabled={isLoading}
                  className="modal-btn rounded-md bg-primary-gradient px-6 py-2 text-sm font-medium text-white/90 transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Creating..." : "Add Promo Code"}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Delete Confirmation Modal */}
      {
        isDeleteModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fade-in px-4"
            onClick={handleDeleteCancel}
          >
            <div
              className="w-full max-w-md rounded-xl bg-navy-800 p-6 shadow-lg animate-fade-in"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="mb-4 text-xl font-bold text-white/90">Delete Promo Code</h2>

              <p className="mb-6 text-sm text-gray-300">
                Are you sure you want to delete this promo code?
              </p>

              {/* Buttons */}
              <div className="flex justify-end gap-4">
                <button
                  onClick={handleDeleteCancel}
                  className="modal-btn rounded-md bg-gray-600 px-6 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                  className="modal-btn rounded-md bg-red-500/90 px-6 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Coin Package Edit Modal */}
      {
        isCoinPackageEditModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fade-in px-4"
            onClick={handleCoinPackageCloseModal}
          >
            <div
              className="w-full max-w-md rounded-xl bg-navy-800 p-6 shadow-lg animate-fade-in relative modal-scrollable"
              style={{ maxHeight: '90vh', overflowY: 'auto' }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={handleCoinPackageCloseModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-white/90 transition-colors"
                title="Close"
              >
                <MdClose className="h-6 w-6" />
              </button>

              <h2 className="mb-4 text-xl font-bold text-white/90 pr-8">Edit Coin Package</h2>

              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-white/90">
                    Package Name
                  </label>
                  <input
                    type="text"
                    value={coinPackageEditFormData.package_name}
                    onChange={(e) => {
                      setCoinPackageEditFormData({
                        ...coinPackageEditFormData,
                        package_name: e.target.value,
                      });
                      if (coinPackageEditFormErrors.package_name) {
                        setCoinPackageEditFormErrors({
                          ...coinPackageEditFormErrors,
                          package_name: "",
                        });
                      }
                    }}
                    className={`w-full rounded-md border ${coinPackageEditFormErrors.package_name
                      ? "border-red-500"
                      : "border-gray-600 hover:border-blue-400"
                      } bg-navy-700 px-3 py-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400`}
                    placeholder="e.g., Starter Pack"
                  />
                  {coinPackageEditFormErrors.package_name && (
                    <p className="mt-1 text-sm text-red-400">
                      {coinPackageEditFormErrors.package_name}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-white/90">
                      Price
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={coinPackageEditFormData.price}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Check if whole number part has more than 6 digits
                        if (value) {
                          const wholeNumberPart = value.split('.')[0];
                          if (wholeNumberPart.length > 6) {
                            setCoinPackageEditFormErrors({
                              ...coinPackageEditFormErrors,
                              price: "Price cannot have more than 6 digits",
                            });
                            return; // Don't update if more than 6 digits
                          }
                        }
                        setCoinPackageEditFormData({
                          ...coinPackageEditFormData,
                          price: value,
                        });
                        if (coinPackageEditFormErrors.price) {
                          setCoinPackageEditFormErrors({
                            ...coinPackageEditFormErrors,
                            price: "",
                          });
                        }
                      }}
                      className={`w-full rounded-md border ${coinPackageEditFormErrors.price
                        ? "border-red-500"
                        : "border-gray-600 hover:border-blue-400"
                        } bg-navy-700 px-3 py-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400`}
                      placeholder="Add price"
                      min="0"
                    />
                    {coinPackageEditFormErrors.price && (
                      <p className="mt-1 text-sm text-red-400">
                        {coinPackageEditFormErrors.price}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-white/90">
                      Coins
                    </label>
                    <input
                      type="number"
                      value={coinPackageEditFormData.coins}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Check if value has more than 6 digits
                        if (value && value.length > 6) {
                          setCoinPackageEditFormErrors({
                            ...coinPackageEditFormErrors,
                            coins: "Coins cannot have more than 6 digits",
                          });
                          return; // Don't update if more than 6 digits
                        }
                        setCoinPackageEditFormData({
                          ...coinPackageEditFormData,
                          coins: value,
                        });
                        if (coinPackageEditFormErrors.coins) {
                          setCoinPackageEditFormErrors({
                            ...coinPackageEditFormErrors,
                            coins: "",
                          });
                        }
                      }}
                      className={`w-full rounded-md border ${coinPackageEditFormErrors.coins
                        ? "border-red-500"
                        : "border-gray-600 hover:border-blue-400"
                        } bg-navy-700 px-3 py-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400`}
                      placeholder="Add coins"
                      min="1"
                    />
                    {coinPackageEditFormErrors.coins && (
                      <p className="mt-1 text-sm text-red-400">
                        {coinPackageEditFormErrors.coins}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-white/90">
                    Description
                  </label>
                  <textarea
                    value={coinPackageEditFormData.description}
                    onChange={(e) => {
                      setCoinPackageEditFormData({
                        ...coinPackageEditFormData,
                        description: e.target.value,
                      });
                      if (coinPackageEditFormErrors.description) {
                        setCoinPackageEditFormErrors({
                          ...coinPackageEditFormErrors,
                          description: "",
                        });
                      }
                    }}
                    rows={1}
                    className={`w-full rounded-md border ${coinPackageEditFormErrors.description
                      ? "border-red-500"
                      : "border-gray-600 hover:border-blue-400"
                      } bg-navy-700 px-3 pt-2 pb-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 resize-none`}
                    placeholder="Add package info"
                  />
                  {coinPackageEditFormErrors.description && (
                    <p className="mt-1 text-sm text-red-400">
                      {coinPackageEditFormErrors.description}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-4">
                <button
                  onClick={handleCoinPackageCloseModal}
                  className="modal-btn rounded-md bg-gray-600 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCoinPackageSaveChanges}
                  disabled={isLoading}
                  className="modal-btn rounded-md bg-primary-gradient px-6 py-2 text-sm font-medium text-white transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Coin Package Add Modal */}
      {
        isCoinPackageAddModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fade-in px-4"
            onClick={handleCoinPackageCloseAddModal}
          >
            <div
              className="w-full max-w-md rounded-xl bg-navy-800 p-6 shadow-lg animate-fade-in relative modal-scrollable"
              style={{ maxHeight: '90vh', overflowY: 'auto' }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={handleCoinPackageCloseAddModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-white/90 transition-colors"
                title="Close"
              >
                <MdClose className="h-6 w-6" />
              </button>

              <h2 className="mb-4 text-xl font-bold text-white/90 pr-8">Add Coin Package</h2>

              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-white/90">
                    Package Name
                  </label>
                  <input
                    type="text"
                    value={coinPackageAddFormData.package_name}
                    onChange={(e) => {
                      setCoinPackageAddFormData({
                        ...coinPackageAddFormData,
                        package_name: e.target.value,
                      });
                      if (coinPackageAddFormErrors.package_name) {
                        setCoinPackageAddFormErrors({
                          ...coinPackageAddFormErrors,
                          package_name: "",
                        });
                      }
                    }}
                    className={`w-full rounded-md border ${coinPackageAddFormErrors.package_name
                      ? "border-red-500"
                      : "border-gray-600 hover:border-blue-400"
                      } bg-navy-700 px-3 py-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400`}
                    placeholder="e.g., Starter Pack"
                  />
                  {coinPackageAddFormErrors.package_name && (
                    <p className="mt-1 text-sm text-red-400">
                      {coinPackageAddFormErrors.package_name}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-white/90">
                      Price
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={coinPackageAddFormData.price}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Check if whole number part has more than 6 digits
                        if (value) {
                          const wholeNumberPart = value.split('.')[0];
                          if (wholeNumberPart.length > 6) {
                            setCoinPackageAddFormErrors({
                              ...coinPackageAddFormErrors,
                              price: "Price cannot have more than 6 digits",
                            });
                            return; // Don't update if more than 6 digits
                          }
                        }
                        setCoinPackageAddFormData({
                          ...coinPackageAddFormData,
                          price: value,
                        });
                        if (coinPackageAddFormErrors.price) {
                          setCoinPackageAddFormErrors({
                            ...coinPackageAddFormErrors,
                            price: "",
                          });
                        }
                      }}
                      className={`w-full rounded-md border ${coinPackageAddFormErrors.price
                        ? "border-red-500"
                        : "border-gray-600 hover:border-blue-400"
                        } bg-navy-700 px-3 py-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400`}
                      placeholder="Add price"
                      min="0"
                    />
                    {coinPackageAddFormErrors.price && (
                      <p className="mt-1 text-sm text-red-400">
                        {coinPackageAddFormErrors.price}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-white/90">
                      Coins
                    </label>
                    <input
                      type="number"
                      value={coinPackageAddFormData.coins}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Check if value has more than 6 digits
                        if (value && value.length > 6) {
                          setCoinPackageAddFormErrors({
                            ...coinPackageAddFormErrors,
                            coins: "Coins cannot have more than 6 digits",
                          });
                          return; // Don't update if more than 6 digits
                        }
                        setCoinPackageAddFormData({
                          ...coinPackageAddFormData,
                          coins: value,
                        });
                        if (coinPackageAddFormErrors.coins) {
                          setCoinPackageAddFormErrors({
                            ...coinPackageAddFormErrors,
                            coins: "",
                          });
                        }
                      }}
                      className={`w-full rounded-md border ${coinPackageAddFormErrors.coins
                        ? "border-red-500"
                        : "border-gray-600 hover:border-blue-400"
                        } bg-navy-700 px-3 py-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400`}
                      placeholder="Add coins"
                      min="1"
                    />
                    {coinPackageAddFormErrors.coins && (
                      <p className="mt-1 text-sm text-red-400">
                        {coinPackageAddFormErrors.coins}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-white/90">
                    Description
                  </label>
                  <textarea
                    value={coinPackageAddFormData.description}
                    onChange={(e) => {
                      setCoinPackageAddFormData({
                        ...coinPackageAddFormData,
                        description: e.target.value,
                      });
                      if (coinPackageAddFormErrors.description) {
                        setCoinPackageAddFormErrors({
                          ...coinPackageAddFormErrors,
                          description: "",
                        });
                      }
                    }}
                    rows={1}
                    className={`w-full rounded-md border ${coinPackageAddFormErrors.description
                      ? "border-red-500"
                      : "border-gray-600 hover:border-blue-400"
                      } bg-navy-700 px-3 pt-2 pb-1.5 text-sm text-white/90 placeholder:text-xs placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 resize-none`}
                    placeholder="Add package info"
                  />
                  {coinPackageAddFormErrors.description && (
                    <p className="mt-1 text-sm text-red-400">
                      {coinPackageAddFormErrors.description}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-4">
                <button
                  onClick={handleCoinPackageCloseAddModal}
                  className="modal-btn rounded-md bg-gray-600 px-6 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddCoinPackageSubmit}
                  disabled={isLoading}
                  className="modal-btn rounded-md bg-primary-gradient px-6 py-2 text-sm font-medium text-white/90 transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Creating..." : "Add Coin Package"}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Coin Package Delete Confirmation Modal */}
      {
        isCoinPackageDeleteModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fade-in px-4"
            onClick={handleCoinPackageDeleteCancel}
          >
            <div
              className="w-full max-w-md rounded-xl bg-navy-800 p-6 shadow-lg animate-fade-in"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="mb-4 text-xl font-bold text-white">Delete Coin Package</h2>

              <p className="mb-6 text-sm text-gray-300">
                Are you sure you want to delete this coin package?
              </p>

              <div className="flex justify-end gap-4">
                <button
                  onClick={handleCoinPackageDeleteCancel}
                  className="modal-btn rounded-md bg-gray-600 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCoinPackageDeleteConfirm}
                  disabled={isDeleting}
                  className="modal-btn rounded-md bg-red-500/90 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Edit Order Modal */}
      {
        isEditOrderModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fade-in px-4"
            onClick={handleCloseEditOrderModal}
          >
            <div
              className="w-full max-w-lg rounded-xl bg-navy-800 p-6 shadow-lg animate-fade-in"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="mb-4">
                <h2 className="text-2xl font-bold text-white/90 mb-2">Reorder Coin Packages</h2>
                <p className="text-sm text-white/70">
                  Drag and drop coin packages to reorder them. The order will be saved when you click 'Save Order'.
                </p>
              </div>

              {/* Modal Content - Sortable List */}
              {reorderCoinPackages.length === 0 ? (
                <div className="py-8 text-center text-white/70">
                  No coin packages found
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleReorderDragStart}
                  onDragOver={handleReorderDragOver}
                  onDragEnd={handleReorderDragEnd}
                >
                  <SortableContext
                    items={reorderCoinPackages.map((item) => item.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div 
                      className="space-y-2 mb-6 overflow-y-auto pr-2 reorder-modal-scroll"
                      style={{ 
                        maxHeight: '400px', // Approximately 5 items height (5 * ~80px per item)
                      }}
                    >
                      {reorderCoinPackages.map((item, index) => {
                        const isDeleted = item.deleted || deletedCoinPackageIds.has(item.id || item._id || item.package_id);
                        const isActiveStatus = isDeleted ? false : (item.status === "active");

                        return (
                          <SortableCoinPackageRow
                            key={item.id}
                            item={item}
                            index={index}
                            isDeleted={isDeleted}
                            isActive={isActiveStatus}
                            activeId={activeId}
                            overId={overId}
                          />
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
              )}

              {/* Modal Footer */}
              <div className="flex justify-end gap-4 pt-4 border-t border-gray-700">
                <button
                  onClick={handleCloseEditOrderModal}
                  disabled={isSavingOrder}
                  className="modal-btn rounded-md bg-gray-600 px-6 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveOrder}
                  disabled={isSavingOrder || reorderCoinPackages.length === 0}
                  className="modal-btn rounded-md bg-primary-gradient px-6 py-2 text-sm font-medium text-white/90 transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingOrder ? "Saving..." : "Save Order"}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Promo Code Edit Order Modal */}
      {
        isPromoCodeEditOrderModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fade-in px-4"
            onClick={handleClosePromoCodeEditOrderModal}
          >
            <div
              className="w-full max-w-lg rounded-xl bg-navy-800 p-6 shadow-lg animate-fade-in"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="mb-4">
                <h2 className="text-2xl font-bold text-white/90 mb-2">Reorder Promo Codes</h2>
                <p className="text-sm text-white/70">
                  Drag and drop promo codes to reorder them. The order will be saved when you click 'Save Order'.
                </p>
              </div>

              {/* Modal Content - Sortable List */}
              {reorderPromoCodes.length === 0 ? (
                <div className="py-8 text-center text-white/70">
                  No promo codes found
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleReorderDragStart}
                  onDragOver={handleReorderDragOver}
                  onDragEnd={handlePromoCodeReorderDragEnd}
                >
                  <SortableContext
                    items={reorderPromoCodes.map((item) => item.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div 
                      className="space-y-2 mb-6 overflow-y-auto pr-2 reorder-modal-scroll"
                      style={{ 
                        maxHeight: '400px', // Approximately 5 items height
                      }}
                    >
                      {reorderPromoCodes.map((item, index) => (
                        <SortablePromoCodeRow
                          key={item.id}
                          item={item}
                          index={index}
                          activeId={activeId}
                          overId={overId}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}

              {/* Modal Footer */}
              <div className="flex justify-end gap-4 pt-4 border-t border-gray-700">
                <button
                  onClick={handleClosePromoCodeEditOrderModal}
                  disabled={isSavingPromoCodeOrder}
                  className="modal-btn rounded-md bg-gray-600 px-6 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePromoCodeOrder}
                  disabled={isSavingPromoCodeOrder || reorderPromoCodes.length === 0}
                  className="modal-btn rounded-md bg-primary-gradient px-6 py-2 text-sm font-medium text-white/90 transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingPromoCodeOrder ? "Saving..." : "Save Order"}
                </button>
              </div>
            </div>
          </div>
        )
      }

    </>
  );
};

export default Promotion;

